import React from "react";
import { compareOperationalRecords } from "../lib/sortUtils";
import { buildNextRecordNumber, mapCostCenterCodes } from "../lib/recordNumberUtils";
import { isAdminNotificationEnabled } from "../lib/adminNotificationPrefs";
import { matchesRecordNumberSearch } from "../lib/recordNumberSearch";
import { buildSupplierDirectory } from "../lib/peopleRoleAdapters";
import { LIVE_ACTIVITY_EVENT_TYPES, LIVE_ACTIVITY_MODULES, notifyOperationalActivity } from "../lib/liveActivities";
import { stampRecordGeneration } from "../lib/farmGenerations";
import { DB, getDeviceId } from "../lib/deviceStorage";
import { APP_VERSION, isNativeShell } from "../config/appConfig";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, appendDeletePendingDisplaySuffix, createOperationalOperationId, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";

const PURCHASE_ITEM_STATUS_OPTIONS = ["Analisando","Aprovado","Negado","Parcial"];

function localDateKey(date = new Date()) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch (_) {
    return new Date().toISOString().slice(0, 10);
  }
}

function InlinePanelShell({ T, title, children }) {
  return (
    <div style={{background:T.card,border:`1px solid ${T.accent}44`,borderRadius:14,padding:"18px 20px",marginBottom:16,boxShadow:"0 10px 24px rgba(2,6,23,.08)"}}>
      <div style={{fontWeight:800,color:T.text,fontSize:15,marginBottom:12}}>{title}</div>
      {children}
    </div>
  );
}

function InlineDenyPanel({ T, denyReasons, Btn, Field, Select, Textarea, onClose, reason:controlledReason, customReason:controlledCustomReason, onReasonChange }) {
  const defaultReason = denyReasons[0]||"Outro motivo";
  const reason = controlledReason || defaultReason;
  const custom = controlledCustomReason || "";
  const emit = React.useCallback((nextReason, nextCustom=custom)=>{
    const finalReason = nextReason === "Outro motivo" ? (String(nextCustom || "").trim() || nextReason) : nextReason;
    onReasonChange?.({ reason:nextReason, customReason:nextCustom, finalReason });
  }, [custom, onReasonChange]);
  React.useEffect(()=>{ emit(reason, custom); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <InlinePanelShell T={T} title="❌ Motivo da Negação">
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>
          Escolha a justificativa, marque um ou mais itens como <b>Negado</b> e use o botão <b>Confirmar</b> no final da página para salvar a decisão.
        </div>
        <Field label="Justificativa *">
          <Select value={reason} onChange={e=>emit(e.target.value, custom)}>
            {denyReasons.map(r=><option key={r}>{r}</option>)}
          </Select>
        </Field>
        {reason==="Outro motivo"&&<Field label="Descreva o motivo"><Textarea rows={2} value={custom} onChange={e=>emit(reason, e.target.value)} placeholder="Descreva o motivo da negação..."/></Field>}
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
        </div>
      </div>
    </InlinePanelShell>
  );
}

function InlineNoQuotePanel({ T, Field, Select, reasons, reason="", onReasonChange }) {
  return (
    <InlinePanelShell T={T} title="⚡ Aprovar sem cotação">
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Selecione a justificativa obrigatória para aprovar esta compra sem cotação registrada.</div>
        <Field label="Justificativa *">
          <Select value={reason} onChange={e=>onReasonChange?.(e.target.value)}>
            <option value="">Selecione...</option>
            {reasons.map(item=><option key={item} value={item}>{item}</option>)}
          </Select>
        </Field>
      </div>
    </InlinePanelShell>
  );
}

function InlineCotacaoPanel({ T, order, suppliers, Field, Input, Select, show, onDraftChange }) {
  const [cSupplier, setCSupplier] = React.useState(suppliers[0]?.name||"");
  const [cPrices, setCPrices] = React.useState(Object.fromEntries((order.items||[]).map(i=>[i.id,""])));
  const [cObs, setCObs] = React.useState("");
  const [cFiles, setCFiles] = React.useState([]);

  React.useEffect(()=>{
    onDraftChange?.({
      supplierName:cSupplier,
      items:(order.items||[]).map(i=>({itemId:i.id,price:Number(cPrices[i.id]||0)})),
      obs:cObs,
      files:cFiles,
      photos:cFiles.map(f=>f.url)
    });
  }, [cSupplier, cPrices, cObs, cFiles, order, onDraftChange]);

  function addCotacaoPdf(){
    const inp=document.createElement("input"); inp.type="file"; inp.accept="application/pdf"; inp.multiple=true;
    inp.onchange=async e=>{
      for(const f of Array.from(e.target.files || [])){
        const dataUrl = await new Promise(r=>{ const fr=new FileReader(); fr.onload=()=>r(fr.result); fr.readAsDataURL(f); });
        setCFiles(p=>[...p,{ id:`pdf-${Date.now()}-${Math.random().toString(36).slice(2,8)}`, name:f.name||"orcamento.pdf", mime:f.type||"application/pdf", url:dataUrl }]);
      }
    };
    inp.click();
  }

  return (
    <InlinePanelShell T={T} title="💰 Registrar Cotação">
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:10,padding:"10px 16px",fontSize:12,color:T.yellow}}>
          📋 Adicione o preço encontrado por fornecedor. Todas as cotações ficam registradas para auditoria. Para anexar orçamento, este módulo aceita somente PDF.
        </div>
        <Field label="Fornecedor *">
          <Select value={cSupplier} onChange={e=>setCSupplier(e.target.value)}>
            <option value="">Selecione o fornecedor...</option>
            {suppliers.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
          </Select>
        </Field>
        <div style={{fontWeight:700,fontSize:13,color:T.text,marginBottom:4}}>Preços por item:</div>
        {(order.items||[]).map(item=>(
          <div key={item.id} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"center",background:T.card2,padding:"10px 14px",borderRadius:8,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:13,color:T.text,fontWeight:500}}>{item.description}</div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{color:T.muted,fontSize:11}}>R$</span>
              <Input type="number" min="0" step="0.01" value={cPrices[item.id]||""} onChange={e=>setCPrices(p=>({...p,[item.id]:e.target.value}))} style={{width:100,textAlign:"right"}} placeholder="0,00"/>
            </div>
          </div>
        ))}
        <Field label="Observação"><Input value={cObs} onChange={e=>setCObs(e.target.value)} placeholder="Prazo de entrega, condições..."/></Field>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:6}}>📄 Orçamento em PDF ({cFiles.length})</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            {cFiles.map((file,i)=>(
              <div key={file.id||i} style={{position:"relative",display:"flex",alignItems:"center",gap:6,padding:"8px 10px",borderRadius:10,border:`1px solid ${T.border}`,background:T.card2,maxWidth:220}}>
                <span style={{fontSize:18}}>📄</span>
                <button type="button" onClick={()=>window.open(file.url||file.dataUrl,"_blank")} style={{background:"none",border:"none",padding:0,color:T.accent,cursor:"pointer",fontSize:11,textAlign:"left",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:150}}>{file.name||`Orçamento ${i+1}.pdf`}</button>
                <button onClick={()=>setCFiles(p=>p.filter((_,j)=>j!==i))} style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",background:T.red,border:"none",color:"#fff",fontSize:11,cursor:"pointer"}}>×</button>
              </div>
            ))}
            <button onClick={addCotacaoPdf} style={{minWidth:72,height:64,padding:"0 12px",borderRadius:10,border:`2px dashed ${T.border}`,background:T.card2,cursor:"pointer",color:T.muted,fontSize:12,fontWeight:700}}>+ PDF</button>
          </div>
          <div style={{fontSize:11,color:T.muted,marginTop:6}}>Somente arquivos PDF são aceitos para orçamento.</div>
        </div>
      </div>
    </InlinePanelShell>
  );
}

export function createPurchasesModule(deps){
  const {
    useState,
    useEffect,
    useRef,
    useSysLists,
    useSync,
    useFarm,
    useFarms,
    useFarmCollection,
    useGlobalCollection,
    getAccounts,
    useToast,
    useReliableSyncOnSave,
    makeSeed,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    canViewCostCenter,
    canEditCostCenter,
    getAllowedCostCentersForModule,
    itemMatchesCostCenterAccess,
    normalizePhotoEntries,
    createClientId,
    fmtMoney,
    fmtDate,
    T,
    COST_CENTERS,
    PURCHASE_CATS,
    UNITS,
    ORDER_STATUSES,
    ORDER_STATUS_C,
    ORDER_DENY_REASONS_DEFAULT,
    SYSTEM_LISTS_DEFAULTS,
    Btn,
    Modal,
    Field,
    Input,
    Select,
    Textarea,
    Toast,
    CotacaoModal,
    ReceiveModal,
    ServicePhotoStatusIcon,
    makeCreateMeta,
    makeUpdateMeta,
    StatCard,
  } = deps;

  return function PurchasesModule({session,isGestor,openOrderId,onClearOpenOrder,purchaseEditWindowConfig,onBridgeBack}){
  const seed=makeSeed();
  const SL = useSysLists();
  const syncCtx = useSync();
  const { fid: currentFarmId, farm: currentFarm } = useFarm();
  const [farms]                = useFarms();
  const[orders,setOrders]      =useFarmCollection("orders",seed.orders);
  const[suppliers,setSuppliers]=useGlobalCollection("suppliers",seed.suppliers);
  const[people]               =useGlobalCollection("people",[]);
  const[stockCatalog,]         =useGlobalCollection("stock",[]);
  const[,setNotifications]     =useFarmCollection("notifications",[]);
  const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", []);
  const adminGlobalsConfig = React.useMemo(()=> (adminGlobalsConfigRows||[])[0] || null, [adminGlobalsConfigRows]);
  const[,setStockMovements]=useFarmCollection("stockMovements",[]);
  const[,setFinancialEntries]=useFarmCollection("financial",seed.financial || []);
  const syncOnSave             =useReliableSyncOnSave();
  const costCenterCodeMap      = React.useMemo(()=>mapCostCenterCodes(SL?.COST_CENTER_CODES || SYSTEM_LISTS_DEFAULTS.COST_CENTER_CODES || []), [SL]);
  const purchaseTypeOptions    = React.useMemo(()=>{
    const source = (Array.isArray(SL?.CATALOG_PRODUCT_TYPES) && SL.CATALOG_PRODUCT_TYPES.length
      ? SL.CATALOG_PRODUCT_TYPES
      : Array.isArray(SL?.PURCHASE_CATS) && SL.PURCHASE_CATS.length
        ? SL.PURCHASE_CATS
        : PURCHASE_CATS
    );
    return source.map(v=>String(v||"").trim()).filter(Boolean);
  }, [SL?.CATALOG_PRODUCT_TYPES, SL?.PURCHASE_CATS]);

  const purchaseToday = React.useCallback(() => localDateKey(), []);

  const orderableProducts = stockCatalog.filter(p=>p.forOrders!==false);
  const supplierDirectory = React.useMemo(()=>buildSupplierDirectory(people, suppliers), [people, suppliers]);

  const[filterStatus,setFs]    =useState("Todos");
  const[filterCC,setFcc]       =useState("Todos");
  const[search,setSearch]      =useState("");
  const[selected,setSelected]  =useState(null);  // viewed order
  const[form,setForm]          =useState(null);   // new/edit order
  const[cotacaoModal,setCotacao]=useState(null);  // {orderId}
  const[cotacaoDraft,setCotacaoDraft]=useState(null); // draft do painel inline de cotação
  const[receiveModal,setReceive]=useState(null);  // {orderId}
  const[denyModal,setDenyModal]=useState(null);   // {orderId, reason, customReason, finalReason}
  const[noQuoteModal,setNoQuoteModal]=useState(null); // {orderId, reason}
  const[approvalDraft,setApprovalDraft]=useState(null); // {orderId, items:{[itemId]:{itemStatus,authorizedQuantity}}}
  const[lightbox,setLightbox]  =useState(null);
  const[toast,show]            =useToast();
  const[purchaseAccounts,setPurchaseAccounts]=useState([]);

  const [cardFilter,setCardFilter]   =useState("all");
  const purchasesBootstrapRef = useRef("");
  const purchasesNormalizeRef = useRef("");
  const purchasesAnalysisStartRef = useRef(new Set());
  const pendingOpenOrderIdRef = useRef(null);
  const ordersRef = useRef([]);
  const consumedLiveHydratedAtRef = useRef("");
  const officializingProvisionalOrdersRef = useRef(false);

  const safeLoadPurchaseShadowRecords = React.useCallback((farmId=currentFarmId)=>{
    try{
      const raw = localStorage.getItem(`fz:${farmId}:orders:_shadow`);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    }catch(_){ return []; }
  }, [currentFarmId]);

  function isPurchaseRecordProvisional(record){
    const rn = String(record?.recordNumber || "").toUpperCase();
    return record?.recordNumberStatus === "provisional" || rn.endsWith("-PRV");
  }

  function getPurchaseDisplayRecordNumber(record){
    const raw = String(record?.recordNumber || record?.id || "").trim();
    if(!raw) return "";
    // v3.2.12: DEL tem precedência — mostrar -DEL enquanto pendingLocalDelete.
    if(record?.pendingLocalDelete === true) return appendDeletePendingDisplaySuffix(raw);
    if(isPurchaseRecordProvisional(record)) return raw;
    if(record?.pendingLocalUpdate || record?.updatePending) return appendUpdatePendingDisplaySuffix(raw);
    return raw;
  }

  function makeProvisionalPurchaseNumber(officialRecordNumber){
    return appendProvisionalSuffix(officialRecordNumber || "");
  }

  function getPurchaseNumberingSource(extraRecords=[]){
    const current = Array.isArray(ordersRef.current) ? ordersRef.current : [];
    const shadow = safeLoadPurchaseShadowRecords(currentFarmId);
    const byId = new Map();
    [...shadow, ...current, ...(Array.isArray(extraRecords)?extraRecords:[])].forEach(record=>{
      if(!record || typeof record !== "object") return;
      const id = String(record?.id || record?.clientId || "").trim();
      if(!id) return;
      if(!byId.has(id) || !isPurchaseRecordProvisional(record)) byId.set(id, record);
    });
    // Para numeração, considerar também tombstones/histórico do shadow para nunca reutilizar número amigável já existente.
    return Array.from(byId.values());
  }

  function buildNextPurchaseRecordNumber({ costCenter, sourceRecords, provisional=false }={}){
    const source = Array.isArray(sourceRecords) ? sourceRecords : getPurchaseNumberingSource();
    const next = buildNextRecordNumber(source, { moduleKey:"purchases", costCenter, costCenterCodeMap });
    if(!provisional) return { ...next, recordNumberStatus:"official" };
    const provisionalRecordNumber = makeProvisionalPurchaseNumber(next.recordNumber);
    return {
      ...next,
      provisionalRecordNumber,
      recordNumber: provisionalRecordNumber,
      recordNumberStatus:"provisional",
    };
  }

  function shouldCreatePurchaseWithProvisionalNumber(){
    // v2.221.00: toda criação operacional local nasce provisória, mesmo online.
    // O servidor é quem confirma, carimba o número definitivo e remove o sufixo -PRV.
    return true;
  }

  function getPurchaseOperationPlatform(){
    try{
      if(isNativeShell()){
        const ua = String(window?.navigator?.userAgent || "").toLowerCase();
        if(/iphone|ipad|ipod|ios/.test(ua)) return "ios";
        if(/android/.test(ua)) return "android";
        return "native";
      }
    }catch(_){}
    return "web";
  }

  function buildPurchaseOperationOriginMeta(){
    const deviceId = (()=>{ try { return String(getDeviceId?.() || "").trim(); } catch(_){ return ""; } })();
    const sessionId = String(session?.sessionId || session?.id || "").trim();
    const accountId = String(session?.accountId || session?.id || "").trim();
    const userId = String(session?.userId || "").trim();
    const username = String(session?.username || "").trim();
    const userName = String(session?.name || "").trim();
    const platform = getPurchaseOperationPlatform();
    const meta = {
      originDeviceId: deviceId,
      originInstallId: deviceId,
      originSessionId: sessionId,
      originPlatform: platform,
      originAppVersion: APP_VERSION,
      originAccountId: accountId,
      originUserId: userId,
      originUsername: username,
      originUserName: userName,
    };
    return {
      ...meta,
      pendingOperationOrigin: { ...meta },
    };
  }

  function ordersOperationQueueKey(farmId){
    const fid = String(farmId || currentFarmId || "").trim();
    return fid ? `fz:${fid}:orders:_operations` : "";
  }

  function compactOrderOperationSnapshot(record={}){
    if(!record || typeof record !== "object") return {};
    const { receipts, cotacoes, items, costCenterSections, ...rest } = record;
    delete rest.photos;
    return {
      ...rest,
      items: Array.isArray(items) ? items.map(item=>({
        id:item?.id,
        clientId:item?.clientId,
        description:item?.description,
        stockItemId:item?.stockItemId,
        category:item?.category,
        subcategory:item?.subcategory,
        unit:item?.unit,
        quantity:item?.quantity,
        itemStatus:item?.itemStatus,
        authorizedQuantity:item?.authorizedQuantity,
        qtyReceived:item?.qtyReceived,
        costCenter:item?.costCenter,
      })) : [],
      costCenterSections: Array.isArray(costCenterSections) ? costCenterSections.map(sec=>({
        id:sec?.id,
        costCenter:sec?.costCenter,
        items:Array.isArray(sec?.items) ? sec.items.map(item=>({
          id:item?.id,
          clientId:item?.clientId,
          description:item?.description,
          stockItemId:item?.stockItemId,
          category:item?.category,
          subcategory:item?.subcategory,
          unit:item?.unit,
          quantity:item?.quantity,
        })) : [],
      })) : [],
      cotacoes: Array.isArray(cotacoes) ? cotacoes.map(c=>({
        id:c?.id,
        supplier:c?.supplier,
        price:c?.price,
        total:c?.total,
        status:c?.status,
        createdAt:c?.createdAt,
        updatedAt:c?.updatedAt,
      })) : [],
      receipts: Array.isArray(receipts) ? receipts.map(r=>({
        id:r?.id,
        at:r?.at,
        by:r?.by,
        status:r?.status,
      })) : [],
    };
  }

  function registerOrderLocalOperation(record, fallbackOperationType="update_order"){
    try{
      const farmId = String(record?.farmId || currentFarmId || "").trim();
      const key = ordersOperationQueueKey(farmId);
      if(!key || !record || typeof record !== "object") return;
      const operationId = String(record?.pendingOperationId || record?.localOperationId || record?.lastLocalOperationId || "").trim();
      if(!operationId) return;
      const now = new Date().toISOString();
      const meta = record?.pendingOperationOrigin && typeof record.pendingOperationOrigin === "object" ? record.pendingOperationOrigin : buildPurchaseOperationOriginMeta();
      const clientId = String(record?.clientId || record?.id || record?.recordId || "").trim();
      const operation = {
        operationId,
        collection:"orders",
        farmId,
        recordId: clientId,
        clientId,
        operationType: String(record?.pendingOperationType || fallbackOperationType || "update_order"),
        originDeviceId: String(record?.originDeviceId || meta?.originDeviceId || ""),
        originInstallId: String(record?.originInstallId || meta?.originInstallId || ""),
        originSessionId: String(record?.originSessionId || meta?.originSessionId || ""),
        originPlatform: String(record?.originPlatform || meta?.originPlatform || getPurchaseOperationPlatform()),
        originAppVersion: String(record?.originAppVersion || meta?.originAppVersion || APP_VERSION),
        originAccountId: String(record?.originAccountId || meta?.originAccountId || ""),
        originUserId: String(record?.originUserId || meta?.originUserId || ""),
        originUsername: String(record?.originUsername || meta?.originUsername || ""),
        originUserName: String(record?.originUserName || meta?.originUserName || ""),
        baseServerRevision: record?.baseServerRevision ?? record?.serverRevision ?? null,
        payloadPatch: compactOrderOperationSnapshot(record),
        status:"pending",
        attempts:0,
        nextRetryAt:null,
        lastError:null,
        createdAt: String(record?.localUpdatedAt || record?.updatedAt || now),
        updatedAt: now,
      };
      Promise.resolve(DB.get(key)).then((rows)=>{
        const list = Array.isArray(rows) ? rows : [];
        const idx = list.findIndex(item=>String(item?.operationId || "") === operationId);
        const next = idx >= 0
          ? list.map((item,i)=>i===idx ? { ...item, ...operation, attempts: Number(item?.attempts || 0), nextRetryAt: item?.nextRetryAt ?? null, lastError: item?.lastError ?? null, status: item?.status || "pending" } : item)
          : [...list, operation];
        return DB.set(key, next);
      }).catch(()=>{});
    }catch(_){}
  }

  function markPurchaseUpdatePending(record, at=new Date().toISOString(), operationType="update_order"){
    const next = markOperationalUpdatePending(record, { userName: session?.name || "", at, operationType, operationMeta: buildPurchaseOperationOriginMeta() });
    registerOrderLocalOperation(next, operationType);
    return next;
  }
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.purchases;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedPurchaseCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("moduleHome", []);
    return Array.isArray(direct) && direct.length ? direct : ["orders","open_orders","missing_approval","in_transit","quotes","denied_orders","approvals","suppliers"];
  }, [getConfiguredSectionCards]);

  useEffect(()=>{
    let cancelled = false;
    Promise.resolve(getAccounts?.()).then((accs)=>{
      if(!cancelled) setPurchaseAccounts(Array.isArray(accs) ? accs : []);
    }).catch(()=>{ if(!cancelled) setPurchaseAccounts([]); });
    return ()=>{ cancelled = true; };
  }, []);


  useEffect(()=>{
    ordersRef.current = Array.isArray(orders) ? orders : [];
  }, [orders]);

  useEffect(()=>{
    // v3.4.05: quando orders reidrata por pull_delta/live cache em sessão aberta,
    // manter o detalhe selecionado alinhado ao registro mais recente. Sem isso, a
    // lista pode atualizar mas o painel aberto continua mostrando snapshot antigo
    // até logout/login ou reabrir o módulo.
    if(!selected) return;
    const selectedKey = String(selected?.clientId || selected?.id || "");
    if(!selectedKey) return;
    const latest = (Array.isArray(orders) ? orders : []).find((o)=>String(o?.clientId || o?.id || "") === selectedKey);
    if(latest && JSON.stringify(latest) !== JSON.stringify(selected)) {
      setSelected(latest);
    }
    if(!latest && (selected?._deleted || selected?.pendingLocalDelete)) {
      setSelected(null);
    }
  }, [orders, selected]);


  useEffect(()=>{
    // v2.221.11: PRV só sai pelo servidor após push confirmado.
    // Não reservar/oficializar número pelo front para não concorrer com sync.php.
    officializingProvisionalOrdersRef.current = false;
  }, [orders, currentFarmId]);


  const appendFinancialEntry = React.useCallback((entry)=>{
    const payload = {
      id:createClientId('fin'),
      farmId: currentFarmId || '',
      automatic:true,
      originModule:'orders',
      originId: entry.originId || '',
      type: entry.type || 'Despesa',
      category: entry.category || 'Compras',
      accountCode: entry.accountCode || '4.1.02',
      accountName: entry.accountName || 'Compra de Insumos e Estoque',
      description: entry.description || '',
      amount: Number(entry.amount||0),
      date: entry.date || purchaseToday(),
      dueDate: entry.dueDate || entry.date || purchaseToday(),
      paymentMethod: entry.paymentMethod || '',
      documentNumber: entry.documentNumber || '',
      costCenter: entry.costCenter || 'Sede',
      counterCostCenter: entry.counterCostCenter || '',
      notes: entry.notes || '',
      justificationMissing: entry.justificationMissing || '',
      taxModel: entry.taxModel || 'Atual',
      metadata: entry.metadata || {},
      ...makeCreateMeta(session?.name||''),
    };
    setFinancialEntries(prev=>dedupeBy([...(prev||[]), payload], item=>item?.id || `${item?.originModule}:${item?.originId}:${item?.description}`));
    syncOnSave('financial');
  }, [currentFarmId, session?.name, setFinancialEntries, syncOnSave, purchaseToday]);

  const dedupeBy = (list, keyFn) => {
    const map = new Map();
    (list || []).forEach((entry, idx) => {
      if (!entry) return;
      const key = keyFn(entry, idx);
      if (!key) return;
      if (!map.has(key)) map.set(key, entry);
    });
    return Array.from(map.values());
  };

  const normalizeSupplier = React.useCallback((sup) => {
    if (!sup) return sup;
    const actor = session?.name || sup?.updatedBy || sup?.createdBy || "";
    return {
      ...sup,
      id: sup.id || createClientId("sup"),
      name: String(sup.name || "").trim(),
      category: sup.category || purchaseTypeOptions[0] || '',
      contact: sup.contact || "",
      email: sup.email || "",
      ...(!sup.createdAt ? makeCreateMeta(actor, {}) : {}),
    };
  }, [session?.name, purchaseTypeOptions]);

  const PURCHASE_ITEM_STATUS_META = {
    "Analisando": { bg:`${T.orange || "#F59E0B"}18`, color:T.orange || "#F59E0B", border:`1px solid ${(T.orange || "#F59E0B")}44` },
    "Aprovado": { bg:`${T.green}18`, color:T.green, border:`1px solid ${T.green}44` },
    "Negado": { bg:`${T.red}18`, color:T.red, border:`1px solid ${T.red}44` },
    "Parcial": { bg:`${T.accent}18`, color:T.accent, border:`1px solid ${T.accent}44` },
  };
  const normalizeItemStatus = React.useCallback((itemStatus, orderStatus="")=>{
    const value = String(itemStatus || "").trim();
    if (PURCHASE_ITEM_STATUS_OPTIONS.includes(value)) return value;
    const order = String(orderStatus || "").trim();
    if (["Negado","Cancelado"].includes(order)) return "Negado";
    if (["Aprovado","Em Trânsito","Concluído"].includes(order)) return "Aprovado";
    return "Analisando";
  }, []);

  const orderHasPendingApproval = React.useCallback((order)=>{
    if (!order) return false;
    const orderStatus = String(order?.status || "").trim();
    if (orderStatus === "Pendente") return true;
    if (["Concluído","Cancelado","Negado"].includes(orderStatus)) return false;
    const items = Array.isArray(order?.items) ? order.items : [];
    if (!items.length) return ["Pendente","Em Análise"].includes(orderStatus);
    return items.some((item)=> normalizeItemStatus(item?.itemStatus || item?.status, orderStatus) === "Analisando");
  }, [normalizeItemStatus]);

  const deriveOrderStatusFromItems = React.useCallback((order, nextItems)=>{
    const currentStatus = String(order?.status || "Pendente").trim() || "Pendente";
    if (["Concluído","Cancelado","Em Trânsito"].includes(currentStatus)) return currentStatus;
    const items = Array.isArray(nextItems) ? nextItems : [];
    if (!items.length) return currentStatus;
    const analysisStarted = !!order?.analysisStartedAt || currentStatus === "Em Análise";
    if (["Pendente","Criado"].includes(currentStatus) && !analysisStarted) return currentStatus;
    const normalizedStatuses = items.map((item)=> normalizeItemStatus(item?.itemStatus || item?.status, currentStatus));
    if (normalizedStatuses.some((status)=> status === "Analisando")) return "Em Análise";
    if (normalizedStatuses.every((status)=> status === "Negado")) return "Negado";
    if (normalizedStatuses.every((status)=> status === "Aprovado")) return "Aprovado";
    return "Parcial";
  }, [normalizeItemStatus]);

  const normalizeOrder = React.useCallback((order) => {
    if (!order) return order;
    const actor = session?.name || order?.updatedBy || order?.createdBy || order?.requester || "";
    const items = dedupeBy((order.items || []).map((item) => {
      const stableItemId = item.id || item.clientId || createClientId("orditem");
      return {
        ...item,
        id: stableItemId,
        clientId: item.clientId || stableItemId,
        quantity: Number(item.quantity || 0),
        price: Number(item.price || 0),
        qtyReceived: Number(item.qtyReceived || 0),
        photos: normalizePhotoEntries((item?.photos || []).length===0 && item?.photo ? [item.photo] : (item?.photos || [])),
        itemStatus: normalizeItemStatus(item?.itemStatus || item?.status, order?.status),
        authorizedQuantity: item?.authorizedQuantity ?? item?.approvedQuantity ?? item?.qtyAuthorized ?? "",
      };
    }), (item) => item?.clientId || item?.id || `${item?.stockItemId||""}::${String(item?.description||"").trim().toLowerCase()}`);
    const cotacoes = dedupeBy((order.cotacoes || []).map((cot) => {
      const stableCotId = cot.id || cot.clientId || createClientId("cot");
      return {
        ...cot,
        id: stableCotId,
        clientId: cot.clientId || stableCotId,
        farmId: cot.farmId || order.farmId || currentFarmId || "",
        items: (cot.items || []).map((item) => ({ ...item, price: Number(item.price || 0) })),
        photos: normalizePhotoEntries((cot?.photos || []).length===0 && cot?.photo ? [cot.photo] : (cot?.photos || [])),
      };
    }), (cot) => cot?.clientId || cot?.id || `${cot?.supplierId||cot?.supplierName||""}::${cot?.ts||cot?.createdAt||""}`);
    const receipts = dedupeBy((order.receipts || []).map((receipt) => {
      const stableReceiptId = receipt.id || receipt.clientId || createClientId("rcv");
      return {
        ...receipt,
        id: stableReceiptId,
        clientId: receipt.clientId || stableReceiptId,
        farmId: receipt.farmId || order.farmId || currentFarmId || "",
        items: (receipt.items || []).map((item) => ({ ...item, qtyReceived: Number(item.qtyReceived || 0) })),
        photos: normalizePhotoEntries((receipt?.photos || []).length===0 && receipt?.photo ? [receipt.photo] : (receipt?.photos || [])),
      };
    }), (receipt) => receipt?.clientId || receipt?.id || `${receipt?.nfNum||""}::${receipt?.ts||receipt?.createdAt||""}`);
    const status = deriveOrderStatusFromItems(order, items);
    const stableOrderId = order.id || order.clientId || createClientId("ord");
    return {
      ...order,
      status,
      id: stableOrderId,
      clientId: order.clientId || stableOrderId,
      farmId: order.farmId || currentFarmId || "",
      items,
      cotacoes,
      receipts,
      notes: order.notes || "",
      ...(!order.createdAt ? makeCreateMeta(actor, {}) : {}),
    };
  }, [currentFarmId, session?.name, normalizeItemStatus, deriveOrderStatusFromItems]);

  const ensureOrderRecordNumbers = React.useCallback((records)=>{
    const baseRecords = Array.isArray(records) ? records.slice() : [];
    const sorted = baseRecords.map((record, index)=>({ record, index, stamp: String(record?.createdAt || record?.updatedAt || index) }))
      .sort((a,b)=> String(a.stamp).localeCompare(String(b.stamp)));
    let changed = false;
    const working = baseRecords.slice();
    sorted.forEach(({ record, index })=>{
      if (!record || record.recordNumber) return;
      const next = buildNextRecordNumber(working.filter(Boolean), { moduleKey:"purchases", costCenter:record?.costCenter, costCenterCodeMap });
      working[index] = { ...record, ...next };
      changed = true;
    });
    return changed ? working : baseRecords;
  }, [costCenterCodeMap]);

  // v3.8.05: impede regressão de "Em Análise" para "Pendente"/"Criado" em registros
  // que já têm flag local de análise iniciada. Protege contra hydrate/pull_delta/ACK
  // que retornam versão antiga do servidor sem firstRecipientOpenedAt.
  // Não cria novo UDT se já há UDT pendente — apenas preserva o status local.
  const protectOrderAnalysisDowngrade = React.useCallback((existingOrder, incomingOrder) => {
    if (!existingOrder || !incomingOrder) return incomingOrder;
    const existingStatus = String(existingOrder?.status || "").trim();
    const incomingStatus = String(incomingOrder?.status || "").trim();
    const existingHasAnalysis = !!(existingOrder?.firstRecipientOpenedAt || existingOrder?.analysisStartedAt);
    const incomingHasAnalysis = !!(incomingOrder?.firstRecipientOpenedAt || incomingOrder?.analysisStartedAt);
    if(
      existingStatus === "Em Análise" &&
      existingHasAnalysis &&
      ["Pendente","Criado"].includes(incomingStatus) &&
      !incomingHasAnalysis
    ) {
      return {
        ...incomingOrder,
        status: existingOrder.status,
        firstRecipientOpenedAt: existingOrder.firstRecipientOpenedAt,
        firstRecipientOpenedBy: existingOrder.firstRecipientOpenedBy,
        firstRecipientOpenedByAccountId: existingOrder.firstRecipientOpenedByAccountId,
        firstRecipientOpenedByUsername: existingOrder.firstRecipientOpenedByUsername,
        firstRecipientOpenedByRole: existingOrder.firstRecipientOpenedByRole,
        firstRecipientOpenedDeviceId: existingOrder.firstRecipientOpenedDeviceId,
        analysisStartedAt: existingOrder.analysisStartedAt,
        analysisStartedBy: existingOrder.analysisStartedBy,
        analysisStartedByAccountId: existingOrder.analysisStartedByAccountId,
        analysisStartedByUsername: existingOrder.analysisStartedByUsername,
        analysisStartedDeviceId: existingOrder.analysisStartedDeviceId,
      };
    }
    return incomingOrder;
  }, []);

  const applyNormalizedOrders = React.useCallback((nextOrders) => {
    const normalized = dedupeBy((nextOrders || []).map(normalizeOrder), (order)=>order?.clientId || order?.id || `${order?.farmId||""}::${order?.date||""}::${order?.requester||""}::${order?.createdAt||""}`);
    const current = Array.isArray(orders) ? orders : [];
    const guarded = normalized.map((incomingOrder) => {
      const id = incomingOrder?.clientId || incomingOrder?.id;
      if(!id) return incomingOrder;
      const existing = current.find(o => (o?.clientId || o?.id) === id);
      return existing ? protectOrderAnalysisDowngrade(existing, incomingOrder) : incomingOrder;
    });
    const enriched = ensureOrderRecordNumbers(guarded);
    setOrders(enriched);
    return enriched;
  }, [normalizeOrder, setOrders, ensureOrderRecordNumbers, orders, protectOrderAnalysisDowngrade]);

  const getApprovalDraftItemsForOrder = React.useCallback((orderId) => {
    return approvalDraft?.orderId === orderId ? (approvalDraft.items || {}) : {};
  }, [approvalDraft]);

  const mergeApprovalDraftIntoItems = React.useCallback((orderId, items=[]) => {
    const draft = getApprovalDraftItemsForOrder(orderId);
    if(!draft || !Object.keys(draft).length) return items;
    return (items || []).map(item=>{
      const d = draft[item.id] || draft[String(item.id)];
      return d ? {...item,...d} : item;
    });
  }, [getApprovalDraftItemsForOrder]);


  const hasUndecidedPurchaseItems = React.useCallback((order, itemsOverride=null) => {
    const baseItems = Array.isArray(itemsOverride) ? itemsOverride : (order?.items || []);
    return baseItems.some(item=>normalizeItemStatus(item?.itemStatus || item?.status, order?.status) === "Analisando");
  }, [normalizeItemStatus]);

  const applyNormalizedSuppliers = React.useCallback((nextSuppliers) => {
    const normalized = dedupeBy((nextSuppliers || []).map(normalizeSupplier), (sup)=>sup?.id || `${String(sup?.name||"").trim().toLowerCase()}::${sup?.email||""}`);
    setSuppliers(normalized);
    return normalized;
  }, [normalizeSupplier, setSuppliers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
    const normalizedOrders = dedupeBy((orders || []).map(normalizeOrder), (order)=>order?.clientId || order?.id || `${order?.farmId||""}::${order?.date||""}::${order?.requester||""}::${order?.createdAt||""}`);
    const normalizedSuppliers = dedupeBy((suppliers || []).map(normalizeSupplier), (sup)=>sup?.id || `${String(sup?.name||"").trim().toLowerCase()}::${sup?.email||""}`);
    const signature = JSON.stringify({
      orders: normalizedOrders.map(o=>({id:o.id,farmId:o.farmId,status:o.status,items:(o.items||[]).length,cotacoes:(o.cotacoes||[]).length,receipts:(o.receipts||[]).length,updatedAt:o.updatedAt,createdAt:o.createdAt})),
      suppliers: normalizedSuppliers.map(s=>({id:s.id,name:s.name,email:s.email,updatedAt:s.updatedAt,createdAt:s.createdAt}))
    });
    if (purchasesNormalizeRef.current === signature) return;
    purchasesNormalizeRef.current = signature;
    const ordersChanged = JSON.stringify(orders || []) !== JSON.stringify(normalizedOrders);
    const suppliersChanged = JSON.stringify(suppliers || []) !== JSON.stringify(normalizedSuppliers);
    if (ordersChanged) {
      // v2.221.26: normalização local não dispara sync.
      // O sync de Compras deve partir apenas de ações explícitas de criação/edição,
      // evitando loop: retorno do servidor -> orders muda -> normaliza -> novo sync.
      applyNormalizedOrders(normalizedOrders);
    }
    if (suppliersChanged) {
      applyNormalizedSuppliers(normalizedSuppliers);
      syncOnSave("suppliers");
    }
  }, [orders, suppliers, currentFarmId, normalizeOrder, normalizeSupplier, syncOnSave, applyNormalizedOrders, applyNormalizedSuppliers]);

  // v2.216.03: removido reparo automático visible<-shadow no boot.
  // A v2.216.02 podia entrar em ciclo de renderização ao abrir Compras.
  // A reversão manual via Auditoria permanece disponível sem reconstrução automática da lista.

  useEffect(()=>{
    if(!currentFarmId) return;
    const key = `${currentFarmId}:${orders.length}`;
    if (purchasesBootstrapRef.current === key) return;
    if (orders.length > 0) {
      purchasesBootstrapRef.current = key;
      return;
    }
    purchasesBootstrapRef.current = key;
    syncCtx?.requestPrioritySync?.({ collection:"orders", farmId:currentFarmId, reason:"module_bootstrap" }).catch(()=>{});
  },[currentFarmId, orders.length, syncCtx]);

  const tryOpenPendingOrder = React.useCallback(()=>{
    const targetId = String(pendingOpenOrderIdRef.current || openOrderId || "").trim();
    if(!targetId) return false;
    const source = Array.isArray(ordersRef.current) ? ordersRef.current : [];
    const ord = source.find(o=>String(o.id)===String(targetId) || String(o.clientId||"")===String(targetId));
    if(ord){
      pendingOpenOrderIdRef.current = null;
      openOrderDetail(ord);
      if(onClearOpenOrder) onClearOpenOrder();
      return true;
    }
    return false;
  // openOrderDetail is intentionally kept out to avoid re-running this deep-link effect on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOrderId, onClearOpenOrder]);

  // Auto-navigate to order when coming from stock movements or Live Activity deep link.
  useEffect(()=>{
    if(!openOrderId) return;
    pendingOpenOrderIdRef.current = openOrderId;

    // v3.4.10: solução híbrida para corrida entre requestPrioritySync e start_analysis.
    //
    // PROBLEMA: requestPrioritySync imediato iniciava pull_delta antes de start_analysis
    // ser gerado. Se pull_delta retornasse antes da abertura do pedido, ele podia resetar
    // o live cache para "Pendente" e buildOperationLog não encontrava o UDT → _oplog vazio.
    //
    // CAMINHO A — Live hidratou o pedido (fz:live-order-hydrated):
    //   handleLiveHydrated → tryOpenPendingOrder → openOrderDetail → start_analysis
    //   atualiza live cache. ENTÃO requestPrioritySync dispara com delay conservador.
    //   pull_delta encontra live cache com UDT → guard protege.
    //
    // CAMINHO B — pull_delta hidratou o pedido (fz:bg:updated / fz:priority-sync-done):
    //   done handler → tryOpenPendingOrder após sync terminar.
    //   Não há pull_delta concorrente. Guard da v3.4.06 cobre ciclos subsequentes.
    //
    // NÃO disparar requestPrioritySync imediatamente quando há openOrderId.
    // Disparar APENAS depois da hidratação por Live ou no fallback de pedido ausente.
    let liveHydratedThisOrder = false;
    let prioritySyncScheduled = false;

    const schedulePrioritySync = (reason, delayMs = 0) => {
      if(prioritySyncScheduled) return;
      if(!currentFarmId || !syncCtx?.requestPrioritySync) return;
      prioritySyncScheduled = true;
      setTimeout(()=>{
        syncCtx.requestPrioritySync({
          collection: "orders",
          farmId: currentFarmId,
          recordId: openOrderId,
          reason,
        }).catch(()=>{});
      }, delayMs);
    };

    // Tentar abrir imediatamente se o pedido já está na lista local.
    // Se abriu: Live/pull_delta já tinha hidratado antes → não precisa de sync agora.
    const openedImmediately = tryOpenPendingOrder();
    if(!openedImmediately) {
      // Pedido ainda ausente. Aguardar Live/pull_delta/fallback.
    }

    // Caminho B: pull_delta / fz:bg:updated / fz:priority-sync-done hidratou.
    const done = (event)=>{
      const detail = event?.detail || {};
      if(detail?.collection && detail.collection !== "orders") return;
      if(liveHydratedThisOrder) return;
      tryOpenPendingOrder();
    };

    // Caminho A: Live hidratou → abrir pedido → esperar start_analysis → ENTÃO sync.
    const onLiveHydratedForSync = (event)=>{
      const detail = event?.detail || {};
      const targetId = String(detail?.recordId || detail?.order?.id || detail?.order?.clientId || "").trim();
      if(!targetId || targetId !== String(openOrderId || "").trim()) return;
      liveHydratedThisOrder = true;
      // Aguardar handleLiveHydrated/tryOpenPendingOrder/start_analysis + margem para live cache/IDB.
      schedulePrioritySync("live_hydrated_post_open", 600);
    };

    const timer = setTimeout(()=>{
      // Fallback: se Live não hidratou E o pedido ainda não está na lista.
      if(!tryOpenPendingOrder() && !liveHydratedThisOrder) {
        schedulePrioritySync("record_not_found_local", 0);
      } else {
        pendingOpenOrderIdRef.current = null;
      }
    }, 4500);

    window.addEventListener("fz:bg:updated", done);
    window.addEventListener("fz:priority-sync-done", done);
    window.addEventListener("fz:live-order-hydrated", onLiveHydratedForSync);
    return ()=>{
      window.removeEventListener("fz:bg:updated", done);
      window.removeEventListener("fz:priority-sync-done", done);
      window.removeEventListener("fz:live-order-hydrated", onLiveHydratedForSync);
      clearTimeout(timer);
    };
  },[openOrderId, tryOpenPendingOrder, currentFarmId, syncCtx]);

  // Deny reasons — read from systemLists or use defaults
  const denyReasons = SL.ORDER_DENY_REASONS || ORDER_DENY_REASONS_DEFAULT;
  const noQuoteReasons = SL.ORDER_NO_QUOTE_REASONS || SYSTEM_LISTS_DEFAULTS.ORDER_NO_QUOTE_REASONS;

  const getDenyReasonForOrder = React.useCallback((orderId) => {
    if (String(denyModal?.orderId || "") !== String(orderId || "")) return "";
    return String(denyModal?.finalReason || denyModal?.reason || denyReasons[0] || "Outro motivo").trim() || "Outro motivo";
  }, [denyModal, denyReasons]);

  const isAdmin = session?.role==="admin";
  const bridgeCostCenter = String(session?.sedeBridge?.enabled ? (session?.sedeBridge?.costCenter || "") : "").trim();
  const bridgeScoped = !!bridgeCostCenter;
  const matchesBridgeCostCenter = React.useCallback((item)=> !bridgeScoped || String(item?.costCenter || "").trim().toLowerCase()===bridgeCostCenter.toLowerCase(), [bridgeScoped, bridgeCostCenter]);
  const canCreate = canCreateInModule(session,"purchases") || canEditInModule(session,"purchases");
  const canEdit = canEditInModule(session,"purchases");
  const canDelete = canDeleteInModule(session,"purchases");
  const allowedCostCenters = React.useMemo(()=> bridgeScoped ? [bridgeCostCenter] : getAllowedCostCentersForModule(session, "purchases"), [bridgeScoped, bridgeCostCenter, session]);
  const canApprove = isGestor || isAdmin || !!session?.canManagePurchases;
  const canApproveWithoutQuote = isAdmin || !!session?.canApprovePurchaseWithoutQuote;
  const block = React.useCallback(()=>show("🔒 Você não tem permissão para realizar esta ação.","error"), [show]);
  const isOwnOrder = React.useCallback(()=>true, []);
  const parseClock = React.useCallback((value, fallback="00:00")=>{
    const str = String(value || fallback);
    const match = str.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return 0;
    const hh = Math.max(0, Math.min(23, Number(match[1]) || 0));
    const mm = Math.max(0, Math.min(59, Number(match[2]) || 0));
    return (hh * 60) + mm;
  }, []);
  const weekDayLabels = React.useMemo(()=>["domingo","segunda-feira","terça-feira","quarta-feira","quinta-feira","sexta-feira","sábado"], []);
  const purchaseWindowConfig = React.useMemo(()=>({
    mode: String(purchaseEditWindowConfig?.purchaseEditWindowMode || purchaseEditWindowConfig?.mode || "always"),
    startDay: Number.isFinite(Number(purchaseEditWindowConfig?.purchaseEditWeekStartDay)) ? Number(purchaseEditWindowConfig.purchaseEditWeekStartDay) : 2,
    startTime: String(purchaseEditWindowConfig?.purchaseEditWeekStartTime || "14:00"),
    endDay: Number.isFinite(Number(purchaseEditWindowConfig?.purchaseEditWeekEndDay)) ? Number(purchaseEditWindowConfig.purchaseEditWeekEndDay) : 2,
    endTime: String(purchaseEditWindowConfig?.purchaseEditWeekEndTime || "12:00"),
  }), [purchaseEditWindowConfig]);
  const isWithinPurchaseEditWindowNow = React.useCallback(()=>{
    if (purchaseWindowConfig.mode !== "weekly") return true;
    const now = new Date();
    const currentMinutes = (now.getDay() * 1440) + (now.getHours() * 60) + now.getMinutes();
    const startMinutes = (purchaseWindowConfig.startDay * 1440) + parseClock(purchaseWindowConfig.startTime, "14:00");
    const endMinutes = (purchaseWindowConfig.endDay * 1440) + parseClock(purchaseWindowConfig.endTime, "12:00");
    if (startMinutes === endMinutes) return true;
    if (startMinutes < endMinutes) return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }, [parseClock, purchaseWindowConfig]);
  const purchaseEditWindowText = React.useMemo(()=>{
    if (purchaseWindowConfig.mode !== "weekly") return "sempre liberado";
    return `de ${weekDayLabels[purchaseWindowConfig.startDay] || "terça-feira"} às ${purchaseWindowConfig.startTime || "14:00"} até ${weekDayLabels[purchaseWindowConfig.endDay] || "terça-feira"} às ${purchaseWindowConfig.endTime || "12:00"}`;
  }, [purchaseWindowConfig, weekDayLabels]);
  const showPurchaseEditWindowClosed = React.useCallback(()=>{
    show(`O prazo para alteração deste pedido acabou. A janela atual é ${purchaseEditWindowText}. Crie um novo pedido para continuar.`, "error");
  }, [purchaseEditWindowText, show]);
  const canUserEditExistingOrder = React.useCallback((order)=>{
    if (isAdmin || isGestor) return true;
    if (!canEdit) return false;
    return isWithinPurchaseEditWindowNow();
  }, [isAdmin, isGestor, canEdit, isWithinPurchaseEditWindowNow]);

  const purchaseRecipientKey = React.useCallback((recipient)=>{
    return String(recipient?.accountId || recipient?.id || recipient?.username || recipient?.userId || "").trim().toLowerCase();
  }, []);

  const normalizePurchaseRecipient = React.useCallback((acc)=>{
    const role = String(acc?.role || "").trim().toLowerCase();
    const username = String(acc?.username || "").trim();
    const accountId = String(acc?.id || acc?.accountId || "").trim();
    return {
      accountId,
      userId:String(acc?.userId || "").trim(),
      username,
      name:String(acc?.name || acc?.displayName || username || accountId || "").trim(),
      role,
      canManagePurchases: role === "admin" || role === "gestor" || !!acc?.canManagePurchases,
      canApprovePurchaseWithoutQuote: role === "admin" || !!acc?.canApprovePurchaseWithoutQuote,
      receivePurchaseNotifications: role === "admin" || !!acc?.receivePurchaseNotifications,
    };
  }, []);

  const currentPurchaseAuthorRecipient = React.useMemo(()=>normalizePurchaseRecipient({
    id: session?.id || session?.accountId || "",
    accountId: session?.accountId || session?.id || "",
    userId: session?.userId || "",
    username: session?.username || "",
    name: session?.name || "",
    displayName: session?.name || "",
    role: session?.role || "solicitante",
    canManagePurchases: true,
    receivePurchaseNotifications: true,
  }), [normalizePurchaseRecipient, session?.accountId, session?.id, session?.name, session?.role, session?.userId, session?.username]);

  const currentPurchaseAuthorKey = React.useMemo(()=>purchaseRecipientKey(currentPurchaseAuthorRecipient), [currentPurchaseAuthorRecipient, purchaseRecipientKey]);

  const purchaseRecipientOptions = React.useMemo(()=>{
    const farmId = String(currentFarmId || "").trim();
    const authorKey = String(currentPurchaseAuthorKey || "").trim().toLowerCase();
    const canAccessFarm = (acc)=>{
      if(String(acc?.role || "").trim().toLowerCase()==="admin") return true;
      const farmIds = Array.isArray(acc?.farmIds) ? acc.farmIds.filter(Boolean).map(String) : [];
      return !farmId || !farmIds.length || farmIds.includes(farmId);
    };
    const map = new Map();
    (purchaseAccounts || []).forEach(acc=>{
      if(!acc || acc.active === false) return;
      const normalized = normalizePurchaseRecipient(acc);
      if(!(normalized.role === "admin" || normalized.role === "gestor" || normalized.canManagePurchases)) return;
      if(!canAccessFarm(acc)) return;
      const key = purchaseRecipientKey(normalized);
      if(!key || key === authorKey || map.has(key)) return;
      map.set(key, normalized);
    });
    return Array.from(map.values()).sort((a,b)=>{
      const weight = { admin:0, gestor:1, solicitante:2, colaborador:2 };
      const aw = Object.prototype.hasOwnProperty.call(weight, a.role) ? weight[a.role] : 3;
      const bw = Object.prototype.hasOwnProperty.call(weight, b.role) ? weight[b.role] : 3;
      if(aw !== bw) return aw - bw;
      return String(a.name || a.username || "").localeCompare(String(b.name || b.username || ""), "pt-BR");
    });
  }, [currentFarmId, currentPurchaseAuthorKey, normalizePurchaseRecipient, purchaseAccounts, purchaseRecipientKey]);

  const defItem = React.useCallback(()=>({id:createClientId("orditem"),description:"",stockItemId:"",category:"Todas",subcategory:"Todas",productSearch:"",unit:"Unid.",quantity:1,price:0,photos:[]}), []);
  const buildSection = React.useCallback((costCenter="")=>({ id:createClientId("ordcc"), costCenter:costCenter||"", items:[defItem()] }), [defItem]);
  const ensureSectionModel = React.useCallback((nextForm)=>{
    if(!nextForm) return nextForm;
    if(Array.isArray(nextForm.costCenterSections) && nextForm.costCenterSections.length) return nextForm;
    const baseCostCenter = nextForm.costCenter || bridgeCostCenter || allowedCostCenters[0] || "";
    return { ...nextForm, costCenterSections:[buildSection(baseCostCenter)] };
  }, [allowedCostCenters, bridgeCostCenter, buildSection]);

  const selectedPurchaseRecipients = React.useCallback((source)=>{
    const raw = Array.isArray(source?.recipients) ? source.recipients : [];
    const authorKey = String(currentPurchaseAuthorKey || "").trim().toLowerCase();
    const map = new Map();
    raw.map(normalizePurchaseRecipient).forEach(rec=>{
      const key = purchaseRecipientKey(rec);
      if(key && key !== authorKey && !map.has(key)) map.set(key, rec);
    });
    return Array.from(map.values());
  }, [currentPurchaseAuthorKey, normalizePurchaseRecipient, purchaseRecipientKey]);

  const buildPurchaseAudienceRecipients = React.useCallback((source)=>{
    const map = new Map();
    const push = (rec)=>{
      const normalized = normalizePurchaseRecipient(rec);
      const key = purchaseRecipientKey(normalized);
      if(key && !map.has(key)) map.set(key, normalized);
    };
    push(currentPurchaseAuthorRecipient);
    selectedPurchaseRecipients(source).forEach(push);
    return Array.from(map.values());
  }, [currentPurchaseAuthorRecipient, normalizePurchaseRecipient, purchaseRecipientKey, selectedPurchaseRecipients]);

  const currentSessionRecipient = React.useMemo(()=>normalizePurchaseRecipient({
    id: session?.id || session?.accountId || "",
    accountId: session?.accountId || session?.id || "",
    userId: session?.userId || "",
    username: session?.username || "",
    name: session?.name || "",
    displayName: session?.name || "",
    role: session?.role || "",
    canManagePurchases: isAdmin || isGestor || !!session?.canManagePurchases,
    receivePurchaseNotifications: true,
  }), [isAdmin, isGestor, normalizePurchaseRecipient, session?.accountId, session?.canManagePurchases, session?.id, session?.name, session?.role, session?.userId, session?.username]);

  const currentSessionRecipientKey = React.useMemo(()=>purchaseRecipientKey(currentSessionRecipient), [currentSessionRecipient, purchaseRecipientKey]);

  const isCurrentUserPurchaseCreator = React.useCallback((order)=>{
    if(!order) return false;
    const sessionName = String(session?.name || "").trim().toLowerCase();
    const sessionUsername = String(session?.username || "").trim().toLowerCase();
    const sessionAccountId = String(session?.accountId || session?.id || "").trim().toLowerCase();
    const creatorNames = [order?.requester, order?.createdBy, order?.createdByName, order?.userName, order?.user].map(v=>String(v || "").trim().toLowerCase()).filter(Boolean);
    const creatorUsernames = [order?.requesterUsername, order?.createdByUsername, order?.username].map(v=>String(v || "").trim().toLowerCase()).filter(Boolean);
    const creatorAccounts = [order?.requesterAccountId, order?.createdByAccountId, order?.accountId].map(v=>String(v || "").trim().toLowerCase()).filter(Boolean);
    if(sessionAccountId && creatorAccounts.includes(sessionAccountId)) return true;
    if(sessionUsername && creatorUsernames.includes(sessionUsername)) return true;
    if(sessionName && creatorNames.includes(sessionName)) return true;
    return false;
  }, [session?.accountId, session?.id, session?.name, session?.username]);

  const isCurrentUserPurchaseRecipient = React.useCallback((order)=>{
    if(!order) return false;
    if(isCurrentUserPurchaseCreator(order)) return false;
    const key = String(currentSessionRecipientKey || "").trim().toLowerCase();
    if(!key) return false;
    const explicit = Array.isArray(order?.recipients)
      ? order.recipients.map(normalizePurchaseRecipient).filter(rec=>purchaseRecipientKey(rec))
      : [];
    if(!explicit.length) return isAdmin || isGestor || !!session?.canManagePurchases;
    return explicit.some(rec=>purchaseRecipientKey(rec)===key);
  }, [currentSessionRecipientKey, isAdmin, isCurrentUserPurchaseCreator, isGestor, normalizePurchaseRecipient, purchaseRecipientKey, session?.canManagePurchases]);

  const purchaseLiveStageRank = React.useCallback((order)=>{
    const status = String(order?.status || "").trim();
    if(status === "Concluído") return 5;
    if(status === "Em Trânsito") return 4;
    if(["Aprovado","Parcial"].includes(status)) return 3;
    if(status === "Em Análise") return 2;
    if(["Negado","Cancelado"].includes(status)) return 99;
    return 1;
  }, []);

  const orderHasAdvancedBeyondInitial = React.useCallback((order)=>{
    if(!order) return false;
    if(purchaseLiveStageRank(order) >= 3) return true;
    if(order?.approvedAt || order?.approvedBy || order?.approvalAt || order?.approvalBy) return true;
    const items = Array.isArray(order?.items) ? order.items : [];
    return items.some(item=>["Aprovado","Parcial","Negado"].includes(normalizeItemStatus(item?.itemStatus || item?.status, order?.status)));
  }, [normalizeItemStatus, purchaseLiveStageRank]);

  const addPurchaseRecipientToForm = React.useCallback((recipientKey)=>{
    const selectedRecipient = purchaseRecipientOptions.find(rec=>purchaseRecipientKey(rec)===String(recipientKey||"").trim().toLowerCase());
    if(!selectedRecipient) return;
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      const current = selectedPurchaseRecipients(next);
      const key = purchaseRecipientKey(selectedRecipient);
      if(current.some(rec=>purchaseRecipientKey(rec)===key)) return next;
      return { ...next, recipients:[...current, selectedRecipient] };
    });
  }, [ensureSectionModel, purchaseRecipientOptions, purchaseRecipientKey, selectedPurchaseRecipients]);

  const removePurchaseRecipientFromForm = React.useCallback((recipientKey)=>{
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      const key = String(recipientKey || "").trim().toLowerCase();
      return { ...next, recipients:selectedPurchaseRecipients(next).filter(rec=>purchaseRecipientKey(rec)!==key) };
    });
  }, [ensureSectionModel, purchaseRecipientKey, selectedPurchaseRecipients]);

  const purchaseRecipientChipStyle = React.useCallback((role)=>{
    const normalizedRole = String(role || "").trim().toLowerCase();
    const color = normalizedRole === "admin" ? (T.red || T.accent) : normalizedRole === "gestor" ? (T.accent || T.blue) : (T.green || T.accent);
    return { background:`${color}18`, border:`1px solid ${color}55`, color, boxShadow:`0 6px 14px ${color}12` };
  }, []);

  const activeOrders = React.useMemo(() =>
    (Array.isArray(orders) ? orders : []).filter(o => {
      // v3.2.12: DEL pendente permanece visível até ACK do servidor.
      if (o?.pendingLocalDelete === true) return true;
      // Tombstones confirmados ficam ocultos.
      return !(o?._deleted || o?.deletedAt || o?.deleted_at);
    }), [orders]);

  const openEditOrder = React.useCallback((order)=>{
    if (!order) return;
    if (isAdmin || isGestor) { setForm({ ...order, _new:false }); return; }
    if (!canEdit) { block(); return; }
    if (!isWithinPurchaseEditWindowNow()) { showPurchaseEditWindowClosed(); return; }
    setForm({ ...order, _new:false });
  }, [isAdmin, isGestor, canEdit, isWithinPurchaseEditWindowNow, showPurchaseEditWindowClosed, block]);

  const filtered=activeOrders.filter(o=>{
    if(!matchesBridgeCostCenter(o)) return false;
    if(!itemMatchesCostCenterAccess(session, "purchases", o)) return false;
    const status = String(o?.status||"");
    if(cardFilter==="orders"){}
    else if(cardFilter==="open_orders" && ["Concluído","Cancelado","Negado"].includes(status)) return false;
    else if(cardFilter==="missing_approval" && !orderHasPendingApproval(o)) return false;
    else if(cardFilter==="in_transit" && status!=="Em Trânsito") return false;
    else if(cardFilter==="quotes" && !(String(o?.status||"")!=="Negado" && (o.cotacoes||[]).length>0)) return false;
    else if(cardFilter==="denied_orders" && String(o?.status||"")!=="Negado") return false;
    else if(cardFilter==="approvals" && !["Em Análise","Em Trânsito","Concluído"].includes(status)) return false;
    else if(cardFilter==="suppliers" && !((o.cotacoes||[]).length>0 || String(o?.supplierName||"").trim())) return false;
    if(filterStatus!=="Todos"&&o.status!==filterStatus)return false;
    if(!bridgeScoped && filterCC!=="Todos"&&o.costCenter!==filterCC)return false;
    const t=search.toLowerCase();
    return matchesRecordNumberSearch(o, t, [
      o?.costCenter,
      o?.requester,
      o?.status,
      o?.supplierName,
      ...(o?.items||[]).map(i=>i?.description),
    ]);
  }).sort(compareOperationalRecords);

  // ── Item helpers ────────────────────────────────────────────────
  const getPhotos = React.useCallback((item)=>{
    if (!item) return [];
    const raw = Array.isArray(item?.photos)
      ? item.photos
      : Array.isArray(item?.itemPhotos)
        ? item.itemPhotos
        : [];
    return raw.filter(Boolean);
  }, []);

  const supportsInlineNew = !!form?._new;
  const visibleOrderableProductsForCostCenter = React.useCallback((costCenter)=>{
    return orderableProducts.filter(p=>{
      const raw = p?.costCenter ?? p?.centerCost ?? p?.cost_center ?? p?.cc ?? p?.moduleCostCenter;
      const many = Array.isArray(p?.costCenters) ? p.costCenters.filter(Boolean) : [];
      if(many.length) return many.includes(costCenter);
      if(Array.isArray(raw)) return raw.includes(costCenter);
      if(!raw || raw === "Todos" || raw === "Global") return true;
      return String(raw).trim() === String(costCenter || "").trim();
    });
  }, [orderableProducts]);
  const canAccessPurchaseCostCenter = React.useCallback((costCenter)=>{
    return canViewCostCenter(session, "purchases", costCenter) && canEditCostCenter(session, "purchases", costCenter);
  }, [session]);
  function addCostCenterSection(){
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      const existing = new Set((next.costCenterSections||[]).map(sec=>String(sec.costCenter||"")));
      const firstAvailable = (bridgeScoped ? [bridgeCostCenter] : allowedCostCenters).find(cc=>!existing.has(String(cc||""))) || (bridgeCostCenter || allowedCostCenters[0] || "");
      return { ...next, costCenterSections:[...(next.costCenterSections||[]), buildSection(firstAvailable)] };
    });
  }
  function removeCostCenterSection(sectionId){
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      const sections = (next.costCenterSections||[]).filter(sec=>sec.id!==sectionId);
      return { ...next, costCenterSections:sections.length?sections:[buildSection(bridgeCostCenter || allowedCostCenters[0] || "")] };
    });
  }
  function updateCostCenterSection(sectionId, patch){
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      return { ...next, costCenterSections:(next.costCenterSections||[]).map(sec=>sec.id===sectionId?{...sec,...patch}:sec) };
    });
  }
  function addSectionItem(sectionId){
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      return { ...next, costCenterSections:(next.costCenterSections||[]).map(sec=>sec.id===sectionId?{...sec,items:[...(sec.items||[]), defItem()]}:sec) };
    });
  }
  function removeSectionItem(sectionId, itemId){
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      return { ...next, costCenterSections:(next.costCenterSections||[]).map(sec=>sec.id===sectionId?{...sec,items:(sec.items||[]).filter(i=>i.id!==itemId)}:sec) };
    });
  }
  function updateSectionItem(sectionId, itemId, key, value){
    setForm(prev=>{
      const next = ensureSectionModel(prev);
      return { ...next, costCenterSections:(next.costCenterSections||[]).map(sec=>sec.id===sectionId?{...sec,items:(sec.items||[]).map(i=>i.id===itemId?{...i,[key]:value}:i)}:sec) };
    });
  }


  // ── Open new order ───────────────────────────────────────────────
  function openNew(){
    if(!canCreate){block();return;}
    const defaultCostCenter = bridgeCostCenter || allowedCostCenters[0] || (SL.COST_CENTERS||COST_CENTERS)[0];
    if(!defaultCostCenter){show("Sem centro de custo liberado para Compras.","error");return;}
    setForm({_new:true,costCenter:defaultCostCenter,date:purchaseToday(),
      requester:session?.name||"",status:"Pendente",notes:"",recipients:[],recipientAccountIds:[],recipientUsernames:[],items:[defItem()],
      costCenterSections:[buildSection(defaultCostCenter)],
      cotacoes:[],receipts:[]});
  }

  async function buildPurchaseNotificationRecipients(orderRecord){
    const explicitRecipients = selectedPurchaseRecipients(orderRecord);
    if(explicitRecipients.length){
      return buildPurchaseAudienceRecipients(orderRecord).map(rec=>({
        accountId:String(rec?.accountId || rec?.id || "").trim(),
        userId:String(rec?.userId || "").trim(),
        username:String(rec?.username || "").trim().toLowerCase(),
        role:String(rec?.role || "").trim().toLowerCase(),
        name:String(rec?.name || "").trim(),
      })).filter(rec=>rec.accountId || rec.username);
    }

    const loadedAccounts = await getAccounts?.();
    const accounts = Array.isArray(loadedAccounts) ? loadedAccounts : [];
    const farmId = String(orderRecord?.farmId || currentFarmId || "").trim();
    const creatorUsername = String(session?.username || "").trim().toLowerCase();
    const creatorAccountId = String(session?.id || "").trim();
    const canAccessFarm = (acc)=>{
      const farmIds = Array.isArray(acc?.farmIds) ? acc.farmIds.filter(Boolean) : [];
      return !farmId || !farmIds.length || farmIds.includes(farmId);
    };
    const recipientsMap = new Map();
    const pushRecipient = (acc, roleOverride="")=>{
      if(!acc) return;
      const accountId = String(acc?.id || acc?.accountId || "").trim();
      const username = String(acc?.username || "").trim().toLowerCase();
      if(!accountId && !username) return;
      const key = accountId || username;
      if(recipientsMap.has(key)) return;
      recipientsMap.set(key, {
        accountId,
        userId: String(acc?.userId || "").trim(),
        username,
        role: String(roleOverride || acc?.role || "").trim().toLowerCase(),
        name: String(acc?.name || "").trim(),
      });
    };
    pushRecipient({ id: creatorAccountId, userId: session?.userId || "", username: creatorUsername, role: session?.role || "solicitante", name: session?.name || orderRecord?.requester || "" }, session?.role || "solicitante");
    accounts.filter(acc=>String(acc?.role || "").trim().toLowerCase()==="admin" && acc?.active!==false).forEach(acc=>pushRecipient(acc, "admin"));
    accounts.filter(acc=>String(acc?.role || "").trim().toLowerCase()==="gestor" && acc?.active!==false && acc?.receivePurchaseNotifications && canAccessFarm(acc)).forEach(acc=>pushRecipient(acc, "gestor"));
    return Array.from(recipientsMap.values());
  }


  function buildPurchaseLiveActivityDisplay(orderRecord={}, meta={}){
    const rawItems = Array.isArray(orderRecord?.items) ? orderRecord.items : [];
    const itemStatuses = rawItems.map(item=>String(item?.itemStatus || item?.status || "").trim()).filter(Boolean);
    let displayBadge = String(meta?.orderStatus || orderRecord?.displayStatus || orderRecord?.status || "Pendente").trim() || "Pendente";
    if(itemStatuses.length){
      const unique = Array.from(new Set(itemStatuses));
      if(unique.length === 1) displayBadge = unique[0];
      else if(itemStatuses.includes("Parcial")) displayBadge = "Parcial";
      else if(itemStatuses.includes("Analisando")) displayBadge = "Analisando";
      else displayBadge = "Parcial";
    }
    const normalizedStatus = String(orderRecord?.status || displayBadge || "").trim();
    const recipientOpened = !!(orderRecord?.firstRecipientOpenedAt || orderRecord?.analysisStartedAt) || normalizedStatus === "Em Análise";
    const hasApprovedOrFinalItem = itemStatuses.some(status=>["Aprovado","Parcial","Negado"].includes(status));
    if(!recipientOpened && !hasApprovedOrFinalItem && ["Pendente","Criado"].includes(normalizedStatus)) {
      displayBadge = "Criado";
    }
    const statusRank = purchaseLiveStageRank({ status: normalizedStatus });
    // v2.218.02: itemStatus "Analisando" nasce junto com o pedido, mas não significa
    // que um destinatário abriu o detalhe. Só usar Cotação após primeira abertura real.
    const itemRank = hasApprovedOrFinalItem ? 3 : (recipientOpened && itemStatuses.includes("Analisando") ? 2 : 0);
    const effectiveRank = Math.max(statusRank, itemRank);
    const stepperLabels = ["Criado", "Cotação", "Aprovado", "Enviado", "Recebido"];
    const stepperCurrent = effectiveRank >= 99 ? "Aprovado"
      : effectiveRank >= 5 ? "Recebido"
      : effectiveRank >= 4 ? "Enviado"
      : effectiveRank >= 3 ? "Aprovado"
      : effectiveRank >= 2 ? "Cotação"
      : "Criado";
    return {
      displayTitle: "Trembo - Compras",
      displayBadge,
      recordCode: orderRecord?.recordNumber || orderRecord?.id || "",
      displayDate: fmtDate(orderRecord?.date || orderRecord?.createdAt || orderRecord?.updatedAt || ""),
      costCenterName: orderRecord?.costCenter || "",
      stepperLabels,
      stepperCurrent,
    };
  }



  function isPurchaseLiveActivityEnabled(){
    const liveSettingsFromAdmin = adminGlobalsConfig?.liveActivitySettings;
    const liveGloballyDisabled = liveSettingsFromAdmin?.enabled === false;
    const liveModuleDisabled = liveSettingsFromAdmin?.modules?.[LIVE_ACTIVITY_MODULES.PURCHASES] === false;
    return !liveGloballyDisabled && !liveModuleDisabled;
  }

  async function appendPurchaseNotification(orderRecord, meta={}){
    try{
      orderRecord = stampRecordGeneration(orderRecord, currentFarm);
      const actor = session?.name || orderRecord?.requester || "Sistema";
      const eventAt = String(meta?.eventAt || orderRecord?.updatedAt || orderRecord?.createdAt || new Date().toISOString()).trim() || new Date().toISOString();
      const liveDisplay = buildPurchaseLiveActivityDisplay(orderRecord, { ...meta, orderStatus: meta?.orderStatus || orderRecord?.status || "Pendente" });
      if(isPurchaseLiveActivityEnabled()){
        notifyOperationalActivity({
          module: LIVE_ACTIVITY_MODULES.PURCHASES,
          eventType: String(meta?.event || "").includes("created") ? LIVE_ACTIVITY_EVENT_TYPES.CREATED : LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED,
          record: orderRecord,
          title: liveDisplay.displayTitle || String(meta?.title || "Atualização de pedido").trim() || "Atualização de pedido",
          status: meta?.orderStatus || orderRecord?.status || "Pendente",
          previousStatus: meta?.previousOrderStatus || "",
          farmId: String(orderRecord?.farmId || currentFarmId || "").trim(),
          farmName: (farms||[]).find(f=>String(f.id)===String(orderRecord?.farmId || currentFarmId || ""))?.name || orderRecord?.farmName || "",
          extraFarmShortCode: ((farms||[]).find(f=>String(f.id)===String(orderRecord?.farmId || currentFarmId || ""))?.shortCode || (farms||[]).find(f=>String(f.id)===String(orderRecord?.farmId || currentFarmId || ""))?.sigla || orderRecord?.farmShortCode || orderRecord?.farmSigla || ""),
          updatedAt: eventAt,
          actor,
          ...liveDisplay,
          extra: { event: String(meta?.event || "order_updated").trim() || "order_updated", eventLabel: String(meta?.eventLabel || "Atualização do pedido").trim() || "Atualização do pedido", recordNumber: orderRecord?.recordNumber || "", ...liveDisplay },
        })?.catch?.(() => {});
      }
      if(!isAdminNotificationEnabled(adminGlobalsConfig, "purchases")) return;
      const recipients = await buildPurchaseNotificationRecipients(orderRecord);
      if(!recipients.length) return;
      const summary = String(meta?.summary || meta?.body || "").trim() || `${actor} atualizou o pedido${orderRecord?.costCenter ? ` · ${orderRecord.costCenter}` : ""}.`;
      const notifId = createClientId("ntf");
      const notification = {
        id:notifId,
        clientId:notifId,
        farmId:String(orderRecord?.farmId || currentFarmId || "").trim(),
        type:"purchase_event",
        event:String(meta?.event || "order_updated").trim() || "order_updated",
        eventLabel:String(meta?.eventLabel || "Atualização do pedido").trim() || "Atualização do pedido",
        title:String(meta?.title || "Atualização de pedido").trim() || "Atualização de pedido",
        summary,
        body: String(meta?.body || summary).trim() || summary,
        orderId: orderRecord?.id || "",
        orderRecordNumber: orderRecord?.recordNumber || "",
        entityType:"purchase_order",
        entityId: orderRecord?.id || "",
        entityRecordNumber: orderRecord?.recordNumber || "",
        costCenter: orderRecord?.costCenter || "",
        orderStatus: meta?.orderStatus || orderRecord?.status || "Pendente",
        previousOrderStatus: meta?.previousOrderStatus || "",
        requester: orderRecord?.requester || actor,
        recipients,
        openedInApp:false,
        readBy:[],
        eventAt,
        ...makeCreateMeta(session?.name || "", { updatedAt:eventAt }),
      };
      setNotifications(prev=>[...(Array.isArray(prev)?prev:[]), notification]);
      syncOnSave("notifications");
    }catch(e){}
  }

  async function createPurchaseNotifications(orderRecord){
    const actor = session?.name || orderRecord?.requester || "Sistema";
    const orderLabel = orderRecord?.recordNumber || orderRecord?.id || "pedido";
    const summary = `${actor} criou o pedido ${orderLabel}${orderRecord?.costCenter ? ` · ${orderRecord.costCenter}` : ""}.`;
    await appendPurchaseNotification(orderRecord, {
      event:"order_created",
      eventLabel:"Pedido criado",
      title:`Novo pedido ${orderRecord?.recordNumber || ""}`.trim(),
      summary,
      body: summary,
      orderStatus: orderRecord?.status || "Pendente",
      eventAt: orderRecord?.updatedAt || orderRecord?.createdAt || new Date().toISOString(),
    });
  }

  async function notifyOrderStatusChange(previousOrder, nextOrder, eventMeta={}){
    const previousStatus = String(previousOrder?.status || "").trim();
    const nextStatus = String(nextOrder?.status || "").trim();
    if(!nextStatus || previousStatus === nextStatus) return;
    const actor = session?.name || nextOrder?.requester || "Sistema";
    const orderLabel = nextOrder?.recordNumber || nextOrder?.id || "";
    const defaultSummary = `${actor} atualizou o pedido ${orderLabel} de ${previousStatus || "sem status"} para ${nextStatus}.`;
    await appendPurchaseNotification(nextOrder, {
      event:eventMeta?.event || "order_status_changed",
      eventLabel:eventMeta?.eventLabel || "Status atualizado",
      title:eventMeta?.title || `${nextOrder?.recordNumber || "Pedido"} ${nextStatus}`,
      summary:eventMeta?.summary || defaultSummary,
      body:eventMeta?.body || eventMeta?.summary || defaultSummary,
      orderStatus:nextStatus,
      previousOrderStatus:previousStatus,
      eventAt:eventMeta?.eventAt || nextOrder?.updatedAt || new Date().toISOString(),
    });
  }

  function startOrderAnalysisIfNeeded(orderRecord){
    if(!orderRecord) return orderRecord;
    const currentStatus = String(orderRecord?.status || "").trim();
    if(!["Pendente","Criado"].includes(currentStatus)) return orderRecord;
    if(!(isAdmin || isGestor)) return orderRecord;
    if(orderRecord?.firstRecipientOpenedAt || orderRecord?.analysisStartedAt) return orderRecord;
    if(orderHasAdvancedBeyondInitial(orderRecord)) return orderRecord;
    if(!isCurrentUserPurchaseRecipient(orderRecord)) return orderRecord;
    const orderId = String(orderRecord?.id || orderRecord?.clientId || "").trim();
    if(!orderId) return orderRecord;
    // v3.8.05: Ref só bloqueia se o registro ainda tem a flag persistida OU
    // já tem UDT pendente para a mesma análise. Se as flags sumiram por
    // sync/ACK, o Ref não deve impedir reaplicação.
    const refHas = purchasesAnalysisStartRef.current.has(orderId);
    const recordAlreadyMarked = !!(orderRecord?.firstRecipientOpenedAt || orderRecord?.analysisStartedAt);
    const hasPendingUdt = !!(orderRecord?.pendingLocalUpdate || orderRecord?.updatePending);
    if(refHas && (recordAlreadyMarked || hasPendingUdt)) return orderRecord;
    // Não marcar o Ref agora — marcar só após confirmar que a mudança foi aplicada.
    const _ts = new Date().toISOString();
    const deviceId = (()=>{ try { return getDeviceId(); } catch (_error) { return ""; } })();
    // v2.221.11: primeira abertura por destinatário é update operacional pendente,
    // mas NÃO altera recordNumber persistido. UDT é apenas visual na UI.
    const openedPatch = {
      status:"Em Análise",
      firstRecipientOpenedAt:_ts,
      firstRecipientOpenedBy:session?.name || "",
      firstRecipientOpenedByAccountId:session?.accountId || session?.id || "",
      firstRecipientOpenedByUsername:session?.username || "",
      firstRecipientOpenedByRole:session?.role || "",
      firstRecipientOpenedDeviceId:deviceId,
      analysisStartedAt:_ts,
      analysisStartedBy:session?.name || "",
      analysisStartedByAccountId:session?.accountId || session?.id || "",
      analysisStartedByUsername:session?.username || "",
      analysisStartedDeviceId:deviceId,
    };
    let nextOrder = null;
    applyNormalizedOrders((orders || []).map(o=>{
      if(String(o?.id) !== orderId && String(o?.clientId || "") !== orderId) return o;
      if(o?.firstRecipientOpenedAt || o?.analysisStartedAt || orderHasAdvancedBeyondInitial(o)) {
        nextOrder = o;
        return o;
      }
      nextOrder = markPurchaseUpdatePending({
        ...o,
        ...openedPatch,
        ...makeUpdateMeta(session?.name || "", { updatedAt:_ts })
      }, _ts, "start_analysis");
      return nextOrder;
    }));
    const updated = nextOrder || markPurchaseUpdatePending({
      ...orderRecord,
      ...openedPatch,
      ...makeUpdateMeta(session?.name || "", { updatedAt:_ts })
    }, _ts, "start_analysis");
    setSelected(updated);
    if(updated === orderRecord || updated?.firstRecipientOpenedAt !== _ts) return updated;
    // v3.8.05: marcar Ref só aqui — após confirmar que firstRecipientOpenedAt foi aplicado.
    purchasesAnalysisStartRef.current.add(orderId);
    syncOnSave("orders");
    notifyOrderStatusChange(orderRecord, updated, {
      event:"order_analysis_started",
      eventLabel:"Pedido em análise",
      title:`Pedido ${updated?.recordNumber || ""} em análise`.trim(),
      summary:`${session?.name || "Admin"} abriu o pedido ${updated?.recordNumber || orderId} pela primeira vez como destinatário.`,
      eventAt:_ts,
    });
    return updated;
  }

  function openOrderDetail(orderRecord){
    if(!orderRecord) return;
    const currentStatus = String(orderRecord?.status || "").trim();
    if((isAdmin || isGestor) && ["Pendente","Criado"].includes(currentStatus)){
      const updated = startOrderAnalysisIfNeeded(orderRecord);
      if(updated !== orderRecord && String(updated?.status || "").trim() === "Em Análise") return;
    }
    setSelected(orderRecord);
  }

  useEffect(()=>{
    // v2.221.38: o listener da Live nunca grava na lista de orders.
    // O DB e o live cache já foram atualizados pelo `injectLiveHydratedOrderLocally`
    // em FarmDashboard, e o `useFarmCollection` recarrega o state via
    // `fz:sync:updated`. Aqui só sinalizamos a abertura do detalhe.
    const handleLiveHydrated = (detail={})=>{
      const targetFarmId = String(detail?.farmId || "").trim();
      if(targetFarmId && currentFarmId && targetFarmId !== String(currentFarmId)) return false;
      const hydratedOrder = detail?.order;
      const targetId = String(detail?.recordId || hydratedOrder?.id || hydratedOrder?.clientId || hydratedOrder?.recordId || "").trim();
      if(!hydratedOrder || typeof hydratedOrder !== "object" || !targetId) return false;
      pendingOpenOrderIdRef.current = targetId;
      window.setTimeout(()=>{
        try { window.dispatchEvent(new CustomEvent("fz:bg:updated", { detail:{ collection:"orders", farmId:currentFarmId, source:"live-order-hydrate" } })); } catch(_) {}
        tryOpenPendingOrder();
      }, 120);
      return true;
    };
    const onLiveHydrated = (event)=>{
      const detail = event?.detail || {};
      const at = String(detail?.at || "");
      if(at) consumedLiveHydratedAtRef.current = at;
      handleLiveHydrated(detail);
    };
    window.addEventListener("fz:live-order-hydrated", onLiveHydrated);
    // Aplica `__fzLastLiveHydratedOrder` no mount, mas SOMENTE:
    //  - se ainda há um openOrderId ativo (evita reaplicar após onClearOpenOrder);
    //  - se o recordId do cache bate com o openOrderId atual;
    //  - se o `at` ainda não foi consumido por este ref (evita reaplicar a cada re-run do effect).
    try {
      const last = window.__fzLastLiveHydratedOrder;
      const lastAt = String(last?.at || "");
      const lastRecordId = String(last?.recordId || last?.order?.id || last?.order?.clientId || "");
      if(last && openOrderId && lastRecordId && lastRecordId === String(openOrderId)
         && lastAt && lastAt !== consumedLiveHydratedAtRef.current) {
        consumedLiveHydratedAtRef.current = lastAt;
        handleLiveHydrated(last);
      }
    } catch(_) {}
    return ()=>window.removeEventListener("fz:live-order-hydrated", onLiveHydrated);
  }, [currentFarmId, openOrderId, tryOpenPendingOrder]);

  // ── Save order (create/edit) ─────────────────────────────────────
  async function saveOrder(){
    if(form?._new && !canCreate){block();return;}
    if(!form?._new){
      if(isAdmin || isGestor){
      } else {
        if(!canEdit){block();return;}
        if(!isWithinPurchaseEditWindowNow()){showPurchaseEditWindowClosed();return;}
      }
    }
    const normalizedForm = ensureSectionModel(form);
    const loggedRequesterName = String(session?.name || "").trim();
    const existingOrderForRequester = !normalizedForm?._new ? (orders || []).find(o=>String(o?.id || "")===String(normalizedForm?.id || "")) : null;
    const lockedRequesterName = normalizedForm?._new ? loggedRequesterName : (existingOrderForRequester?.requester || loggedRequesterName || normalizedForm?.requester || "");
    const sectionItems = (normalizedForm?.costCenterSections||[]).flatMap(sec=>(sec?.items||[]).map(item=>({ ...item, costCenter: sec.costCenter })));
    const uniqueCenters = Array.from(new Set((normalizedForm?.costCenterSections||[]).map(sec=>String(sec?.costCenter||"").trim()).filter(Boolean)));
    if(uniqueCenters.some(cc=>!canViewCostCenter(session,"purchases",cc))){show("Usuário sem acesso a um dos centros de custo selecionados. Procure o administrador para liberar acesso.","error");return;}
    if(uniqueCenters.some(cc=>!canEditCostCenter(session,"purchases",cc))){show("Usuário sem permissão de compras em um dos centros de custo selecionados.","error");return;}
    if(!sectionItems.length||sectionItems.some(i=>!i.description?.trim())){show("Descreva todos os itens.","error");return;}
    const chosenRecipients = selectedPurchaseRecipients(normalizedForm);
    if(normalizedForm?._new && !chosenRecipients.some(rec=>String(rec?.role || "").trim().toLowerCase()==="admin")){
      show("Selecione pelo menos 1 admin como destinatário do pedido.", "error");
      return;
    }
    const inferredCostCenter = uniqueCenters.length===1 ? uniqueCenters[0] : (uniqueCenters[0] || normalizedForm?.costCenter || "");
    const{_new,...d}=normalizedForm;
    d.requester = lockedRequesterName;
    d.costCenter = inferredCostCenter;
    d.items = sectionItems;
    const liveActivityAudience = buildPurchaseAudienceRecipients({ ...normalizedForm, recipients: chosenRecipients });
    d.recipients = chosenRecipients;
    d.recipientAccountIds = chosenRecipients.map(rec=>String(rec?.accountId || "").trim()).filter(Boolean);
    d.recipientUsernames = chosenRecipients.map(rec=>String(rec?.username || "").trim().toLowerCase()).filter(Boolean);
    d.liveActivityAudience = liveActivityAudience;
    d.liveActivityAudienceAccountIds = liveActivityAudience.map(rec=>String(rec?.accountId || "").trim()).filter(Boolean);
    d.liveActivityAudienceUsernames = liveActivityAudience.map(rec=>String(rec?.username || "").trim().toLowerCase()).filter(Boolean);
    const _ts=new Date().toISOString();
    if(_new){
      const orderClientId = createClientId("ord");
      const nextRecordNumber = buildNextPurchaseRecordNumber({ costCenter:d?.costCenter, provisional: shouldCreatePurchaseWithProvisionalNumber() });
      const newOrder=markOperationalCreatePending(stampRecordGeneration({
        ...d,
        id:orderClientId,
        clientId:orderClientId,
        farmId:currentFarmId||d.farmId||"",
        requesterAccountId:session?.accountId || session?.id || d?.requesterAccountId || "",
        requesterUsername:session?.username || d?.requesterUsername || "",
        createdByAccountId:session?.accountId || session?.id || d?.createdByAccountId || "",
        createdByUsername:session?.username || d?.createdByUsername || "",
        items:(d.items||[]).map(item=>{ const stableItemId = item.id || item.clientId || createClientId("orditem"); return ({ ...item, id:stableItemId, clientId:item.clientId || stableItemId, itemStatus: normalizeItemStatus(item?.itemStatus || item?.status, d?.status), authorizedQuantity: item?.authorizedQuantity ?? item?.approvedQuantity ?? item?.qtyAuthorized ?? "" }); }),
        cotacoes:[],
        receipts:[],
        ...nextRecordNumber,
         ...makeCreateMeta(session?.name||"", { updatedAt:_ts })
      }, currentFarm), { userName: session?.name || "", at: _ts, operationMeta: buildPurchaseOperationOriginMeta() });
      registerOrderLocalOperation(newOrder, "create");
      applyNormalizedOrders([...(orders || []), newOrder]);
      await createPurchaseNotifications(newOrder);
    } else {
      applyNormalizedOrders((orders || []).map(o=>o.id===d.id?markPurchaseUpdatePending({
        ...o,
        ...d,
        farmId:o?.farmId||currentFarmId||d.farmId||"",
        items:(d.items||[]).map(item=>{ const stableItemId = item.id || item.clientId || createClientId("orditem"); return ({ ...item, id:stableItemId, clientId:item.clientId || stableItemId, itemStatus: normalizeItemStatus(item?.itemStatus || item?.status, d?.status), authorizedQuantity: item?.authorizedQuantity ?? item?.approvedQuantity ?? item?.qtyAuthorized ?? "" }); }),
        ...makeUpdateMeta(session?.name||"", { updatedAt:_ts })
      }, _ts, "edit_order"):o));
    }
    show("✅ Pedido salvo!"); setForm(null); syncOnSave("orders");
  }

  function updateOrderItemStatusDraft(orderId, itemId, nextStatus){
    if(!canApprove){block();return;}
    const normalizedStatus = normalizeItemStatus(nextStatus);
    const targetOrder = (orders || []).find(o=>o.id===orderId);
    const targetItem = (targetOrder?.items || []).find(item=>String(item.id)===String(itemId));
    let authorizedQuantity = targetItem?.authorizedQuantity ?? targetItem?.approvedQuantity ?? targetItem?.qtyAuthorized ?? "";
    if(normalizedStatus === "Parcial"){
      const currentQty = String(authorizedQuantity || "").trim() || String(targetItem?.quantity || "").trim();
      const answer = window.prompt("Digite a quantidade autorizada para este item.\nStatus Parcial = quantidade aprovada menor que a quantidade pedida.", currentQty);
      if(answer===null) return;
      const typed = String(answer || "").trim().replace(",", ".");
      if(!typed || Number.isNaN(Number(typed)) || Number(typed) < 0){
        show("Informe uma quantidade autorizada válida para marcar como Parcial.","error");
        return;
      }
      authorizedQuantity = typed;
    }
    if(normalizedStatus === "Aprovado"){
      authorizedQuantity = String(targetItem?.quantity ?? authorizedQuantity ?? "").trim();
    }
    if(normalizedStatus === "Negado" || normalizedStatus === "Analisando"){
      authorizedQuantity = "";
    }

    setApprovalDraft(prev=>{
      const base = prev?.orderId === orderId ? (prev.items || {}) : {};
      return {
        orderId,
        items:{...base,[itemId]:{itemStatus:normalizedStatus,authorizedQuantity}}
      };
    });

    if(normalizedStatus === "Negado" && String(denyModal?.orderId || "") !== String(orderId)){
      setDenyModal({orderId, reason:denyReasons[0]||"Outro motivo", customReason:"", finalReason:denyReasons[0]||"Outro motivo"});
    }

    show(normalizedStatus === "Parcial"
      ? `Item marcado como Parcial · ${authorizedQuantity}. Confirme para salvar.`
      : `Item marcado como ${normalizedStatus}. Confirme para salvar.`
    );
  }

  function confirmItemDecisions(orderId){
    if(!canApprove){block();return;}
    const draft = approvalDraft?.orderId === orderId ? (approvalDraft.items || {}) : {};
    if(!Object.keys(draft).length){
      show("Nenhuma decisão pendente para confirmar.","error");
      return;
    }
    const _ts=new Date().toISOString();
    let previousOrder = null;
    let nextOrder = null;
    applyNormalizedOrders((orders || []).map(o=>{
      if(o.id!==orderId) return o;
      previousOrder = o;
      const denyReason = getDenyReasonForOrder(orderId);
      const nextItemsRaw = (o.items||[]).map(item=>{
        const d = draft[item.id] || draft[String(item.id)];
        return d ? {...item,...d} : item;
      });
      const hasDeniedItems = nextItemsRaw.some(item=>normalizeItemStatus(item?.itemStatus || item?.status, o?.status) === "Negado");
      const allDeniedItems = nextItemsRaw.length > 0 && nextItemsRaw.every(item=>normalizeItemStatus(item?.itemStatus || item?.status, o?.status) === "Negado");
      const nextItems = hasDeniedItems ? nextItemsRaw.map(item=>{
        const itemStatus = normalizeItemStatus(item?.itemStatus || item?.status, o?.status);
        return itemStatus === "Negado" ? { ...item, denyReason, deniedBy:session?.name, deniedAt:_ts } : item;
      }) : nextItemsRaw;
      const nextStatus = deriveOrderStatusFromItems(o, nextItems);
      nextOrder = markPurchaseUpdatePending({
        ...o,
        status: nextStatus,
        items: nextItems,
        ...(hasDeniedItems ? { denyReason, deniedBy:session?.name, deniedAt:_ts } : {}),
        ...(allDeniedItems ? { status:"Negado" } : {}),
        ...makeUpdateMeta(session?.name||"", { updatedAt:_ts })
      }, _ts, hasDeniedItems ? "confirm_item_denials" : "confirm_item_decisions");
      return nextOrder;
    }));
    setApprovalDraft(null);
    if(denyModal?.orderId === orderId) setDenyModal(null);
    show("✅ Decisão dos itens salva!");
    syncOnSave("orders");
    notifyOrderStatusChange(previousOrder, nextOrder, {
      event:"order_item_decisions_confirmed",
      eventLabel:"Decisão dos itens confirmada",
      title:`Pedido ${nextOrder?.status || "atualizado"}`,
      summary:`${session?.name || "Admin"} confirmou a decisão dos itens do pedido ${nextOrder?.recordNumber || orderId}.`,
      eventAt:_ts,
    });
    if(previousOrder?.status === nextOrder?.status && nextOrder){
      const liveDisplay = buildPurchaseLiveActivityDisplay(nextOrder, { orderStatus: nextOrder?.status });
      if(isPurchaseLiveActivityEnabled()){
        notifyOperationalActivity({
          module: LIVE_ACTIVITY_MODULES.PURCHASES,
          eventType: LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED,
          record: nextOrder,
          farmId: String(nextOrder?.farmId || currentFarmId || ""),
          farmName: (farms||[]).find(f=>String(f.id)===String(nextOrder?.farmId || currentFarmId || ""))?.name || nextOrder?.farmName || "",
          extraFarmShortCode: ((farms||[]).find(f=>String(f.id)===String(nextOrder?.farmId || currentFarmId || ""))?.shortCode || (farms||[]).find(f=>String(f.id)===String(nextOrder?.farmId || currentFarmId || ""))?.sigla || nextOrder?.farmShortCode || nextOrder?.farmSigla || ""),
          priority: nextOrder?.priority || "normal",
          ...liveDisplay,
        })?.catch?.(()=>{});
      }
    }
  }

  // ── Add cotação (quote) ──────────────────────────────────────────
  // cotacao = {id, supplierId, supplierName, items:[{itemId,price,obs}], photos:[], by, ts}
  function saveCotacao(orderId, cotacao){
    const _ts=new Date().toISOString();
    let previousOrder = null;
    let nextOrder = null;
    applyNormalizedOrders((orders || []).map(o=>{
      if(o.id!==orderId) return o;
      previousOrder = o;
      const cotacoes=[...(o.cotacoes||[]),{...cotacao,id:createClientId("cot"),ts:_ts,by:session?.name||"",farmId:o?.farmId||currentFarmId||""}];
      const nextItems = mergeApprovalDraftIntoItems(orderId, o.items||[]);
      const status=o.status==="Pendente"?"Em Análise":deriveOrderStatusFromItems(o, nextItems);
      nextOrder = markPurchaseUpdatePending({...o,cotacoes,status,items:nextItems,...makeUpdateMeta(session?.name||"", { updatedAt:_ts })}, _ts, "add_quote");
      return nextOrder;
    }));
    if(approvalDraft?.orderId === orderId) setApprovalDraft(null);
    if(cotacaoModal?.orderId === orderId) setCotacaoDraft(null);
    show("✅ Cotação registrada! Status → Em Análise"); syncOnSave("orders");
    notifyOrderStatusChange(previousOrder, nextOrder, { event:"order_quote_added", eventLabel:"Cotação registrada", title:"Pedido em cotação", summary:`${session?.name || "Sistema"} registrou cotação no pedido ${nextOrder?.recordNumber || orderId}.`, eventAt:_ts });
  }

  // ── Approve order ────────────────────────────────────────────────
  function approveOrder(orderId, itemApprovals){
    // itemApprovals = [{itemId, cotacaoId, supplierId, supplierName, approvedPrice}]
    if(!canApprove){block();return;}
    const targetOrder = (orders || []).find(o=>o.id===orderId);
    const mergedItemsForValidation = mergeApprovalDraftIntoItems(orderId, targetOrder?.items || []);
    const hasUndecidedItems = hasUndecidedPurchaseItems(targetOrder, mergedItemsForValidation);
    if(hasUndecidedItems){
      show("❌ Defina o status de cada item (Aprovado, Parcial ou Negado) antes de aprovar o pedido.","error");
      return;
    }
    const _ts=new Date().toISOString();
    let previousOrder = null;
    let nextOrder = null;
    applyNormalizedOrders((orders || []).map(o=>{
      if(o.id!==orderId) return o;
      previousOrder = o;
      const baseItems = mergeApprovalDraftIntoItems(orderId, o.items||[]);
      const items=baseItems.map(item=>{
        const approval=itemApprovals.find(a=>String(a.itemId)===String(item.id));
        return approval?{...item,...approval}:item;
      });
      nextOrder = markPurchaseUpdatePending({...o,items,status:"Em Trânsito",approvedBy:session?.name,approvedAt:_ts,...makeUpdateMeta(session?.name||"", { updatedAt:_ts })}, _ts, "approve_order");
      return nextOrder;
    }));
    if(approvalDraft?.orderId === orderId) setApprovalDraft(null);
    show("✅ Pedido Aprovado! Status → Em Trânsito"); syncOnSave("orders");
    notifyOrderStatusChange(previousOrder, nextOrder, { event:"order_approved", eventLabel:"Pedido aprovado", title:"Pedido em trânsito", summary:`${session?.name || "Sistema"} aprovou o pedido ${nextOrder?.recordNumber || orderId}.`, eventAt:_ts });
  }

  function approveWithoutQuote(orderId, reason){
    if(!canApprove){block();return;}
    if(!canApproveWithoutQuote){show("🔒 Você não tem permissão para aprovar compra sem cotação.","error");return;}
    if(!reason){show("Selecione uma justificativa para aprovar sem cotação.","error");return;}
    const targetOrder = (orders || []).find(o=>o.id===orderId);
    const mergedItemsForValidation = mergeApprovalDraftIntoItems(orderId, targetOrder?.items || []);
    if(hasUndecidedPurchaseItems(targetOrder, mergedItemsForValidation)){
      show("❌ Defina o status de cada item (Aprovado, Parcial ou Negado) antes de aprovar sem cotação.","error");
      return;
    }
    const _ts=new Date().toISOString();
    let previousOrder = null;
    let nextOrder = null;
    applyNormalizedOrders((orders || []).map(o=>{
      if(o.id!==orderId) return o;
      previousOrder = o;
      const nextItems = mergeApprovalDraftIntoItems(orderId, o.items||[]);
      nextOrder = markPurchaseUpdatePending({
        ...o,
        items:nextItems,
        status:"Em Trânsito",
        approvedWithoutQuote:true,
        noQuoteReason:reason,
        approvedBy:session?.name,
        approvedAt:_ts,
        ...makeUpdateMeta(session?.name||"", { updatedAt:_ts })
      }, _ts, "approve_without_quote");
      return nextOrder;
    }));
    if(approvalDraft?.orderId === orderId) setApprovalDraft(null);
    setNoQuoteModal(null);
    show("✅ Pedido aprovado sem cotação com justificativa registrada.");
    syncOnSave("orders");
    notifyOrderStatusChange(previousOrder, nextOrder, { event:"order_approved_without_quote", eventLabel:"Aprovado sem cotação", title:"Pedido em trânsito", summary:`${session?.name || "Sistema"} aprovou sem cotação o pedido ${nextOrder?.recordNumber || orderId}.`, eventAt:_ts });
  }

  function cancelUnifiedPurchaseFlow(orderId){
    if(approvalDraft?.orderId === orderId) setApprovalDraft(null);
    if(cotacaoModal?.orderId === orderId){ setCotacao(null); setCotacaoDraft(null); }
    if(noQuoteModal?.orderId === orderId) setNoQuoteModal(null);
    show("Alterações pendentes canceladas.");
  }

  function confirmUnifiedPurchaseFlow(orderId){
    if(noQuoteModal?.orderId === orderId){
      approveWithoutQuote(orderId, noQuoteModal?.reason || "");
      return;
    }
    if(cotacaoModal?.orderId === orderId){
      if(!cotacaoDraft?.supplierName){
        show("Selecione o fornecedor.","error");
        return;
      }
      saveCotacao(orderId, cotacaoDraft);
      setCotacao(null);
      setCotacaoDraft(null);
      return;
    }
    if(approvalDraft?.orderId === orderId){
      confirmItemDecisions(orderId);
      return;
    }
    if(denyModal?.orderId === orderId){
      show("Marque pelo menos um item como Negado antes de confirmar a negação.","error");
      return;
    }
    show("Nenhuma alteração pendente para confirmar.","error");
  }

  // ── Deny order: handled by item decisions + final confirmation.

  // ── Receive items (partial or full) ─────────────────────────────
  function receiveItems(orderId, receiptData){
    // receiptData = {nfNum, nfPhoto, items:[{itemId,qtyReceived,condition,expiry}]}
    const _ts=new Date().toISOString();
    const order=orders.find(o=>o.id===orderId);
    if(!order) return;

    // Update items with received quantities
    const updatedItems=(order.items||[]).map(item=>{
      const rd=receiptData.items.find(r=>String(r.itemId)===String(item.id));
      if(!rd) return item;
      const totalReceived=(item.qtyReceived||0)+Number(rd.qtyReceived||0);
      return{...item,qtyReceived:totalReceived,lastCondition:rd.condition,lastExpiry:rd.expiry};
    });

    // Add stock movements for each received item
    const newMovements=[];
    receiptData.items.forEach(rd=>{
      if(!Number(rd.qtyReceived)) return;
      const item=order.items.find(i=>String(i.id)===String(rd.itemId));
      if(!item?.stockItemId) return;
      newMovements.push({
        id:createClientId("smv"),
        itemId:item.stockItemId,
        farmId:order.farmId||currentFarmId||"",
        type:"entrada",
        qty:Number(rd.qtyReceived),
        notes:`Pedido ${order.recordNumber || order.id} — NF ${receiptData.nfNum||"s/n"}`,
        by:session?.name||"—",
        costCenter:order.costCenter,
        nfNum:receiptData.nfNum,
        ts:_ts,
        ...makeCreateMeta(session?.name||"", { updatedAt:_ts }),
      });
    });
    if(newMovements.length>0){
      setStockMovements(p=>[...p,...newMovements]);
      syncOnSave("stockMovements");
    }

    const estimatedReceiptTotal = receiptData.items.reduce((sum, rd)=>{
      const item=order.items.find(i=>String(i.id)===String(rd.itemId));
      const unitPrice = Number(item?.approvedPrice || item?.price || 0);
      return sum + (Number(rd.qtyReceived||0) * unitPrice);
    }, 0);
    const receiptTotal = Number(receiptData?.valueOverride || 0) > 0 ? Number(receiptData.valueOverride || 0) : estimatedReceiptTotal;
    if(receiptTotal > 0 || receiptData?.nfNum || receiptData?.paymentMethod || receiptData?.justificationMissing){
      appendFinancialEntry({
        originId: `${order.id}:receipt:${_ts}`,
        type:'Despesa', category:'Compras', accountCode:'4.1.02', accountName:'Compra de Insumos e Estoque',
        description:`Recebimento do pedido ${order.recordNumber || order.id}`,
        amount: receiptTotal,
        date: receiptData?.date || purchaseToday(),
        dueDate: receiptData?.dueDate || receiptData?.date || purchaseToday(),
        paymentMethod: receiptData?.paymentMethod || '',
        documentNumber: receiptData?.nfNum || '',
        costCenter: order.costCenter || 'Sede',
        counterCostCenter: receiptData?.targetCostCenter || '',
        status: receiptData?.paymentMethod ? 'Agendado' : 'Lançado',
        notes: `Centro de custo origem: ${order.costCenter||'Sede'}${receiptData?.targetCostCenter?` · destino: ${receiptData.targetCostCenter}`:''}${Number(receiptData?.valueOverride||0)>0?' · valor final informado manualmente':''}`,
        justificationMissing: receiptData?.justificationMissing || '',
        taxModel: receiptData?.taxModel || 'Atual',
        metadata:{
          receiptItems: receiptData.items || [],
          orderId: order.id,
          nfNum: receiptData?.nfNum || '',
          nfPhotos: receiptData?.nfPhotos || [],
          estimatedAmount: estimatedReceiptTotal,
          finalAmount: receiptTotal,
          targetCostCenter: receiptData?.targetCostCenter || '',
          receiveDate: receiptData?.date || purchaseToday(),
          stage: 'receipt'
        },
      });
    }

    // Check if all items fully received
    const allReceived=updatedItems.every(i=>Number(i.qtyReceived||0)>=Number(i.quantity||0));
    const receipts=[...(order.receipts||[]),{...receiptData,id:createClientId("rcv"),by:session?.name,ts:_ts,farmId:order.farmId||currentFarmId||"",estimatedAmount:estimatedReceiptTotal,finalAmount:receiptTotal}];
    const status=allReceived?"Concluído":"Em Trânsito";

    let previousOrder = null;
    let nextOrder = null;
    applyNormalizedOrders((orders || []).map(o=>{
      if(o.id!==orderId) return o;
      previousOrder = o;
      nextOrder = markPurchaseUpdatePending({...o,items:updatedItems,receipts,status,...makeUpdateMeta(session?.name||"", { updatedAt:_ts })}, _ts, "receive_items");
      return nextOrder;
    }));
    show(allReceived?"✅ Pedido Concluído! Estoque atualizado.":"📦 Itens recebidos parcialmente. Estoque atualizado.");
    setReceive(null); syncOnSave("orders");
    notifyOrderStatusChange(previousOrder, nextOrder, { event:allReceived?"order_completed":"order_receipt_registered", eventLabel:allReceived?"Pedido concluído":"Recebimento registrado", title:allReceived?"Pedido concluído":"Pedido atualizado", summary:allReceived?`${session?.name || "Sistema"} concluiu o pedido ${nextOrder?.recordNumber || orderId}.`:`${session?.name || "Sistema"} registrou recebimento no pedido ${nextOrder?.recordNumber || orderId}.`, eventAt:_ts });
  }

  // ── Delete order ─────────────────────────────────────────────────
  function del(id){
    if(!isAdmin){show("Apenas administrador pode excluir pedidos.","error");return;}
    const deletedAt = new Date().toISOString();
    const operationId = createOperationalOperationId("delete");
    let pendingDeleteRecord = null;
    // v3.2.12: NÃO aplicar _deleted:true localmente. O registro permanece visível
    // com sufixo -DEL e badge enquanto aguarda ACK do servidor.
    applyNormalizedOrders((orders || []).map(o=>{
      if(String(o.id)!==String(id)) return o;
      pendingDeleteRecord = {
        ...o,
        ...buildPurchaseOperationOriginMeta(),
        localOperationId: operationId,
        lastLocalOperationId: operationId,
        lastLocalOperationType: "delete",
        pendingOperationId: operationId,
        pendingOperationType: "delete",
        pendingLocalChange: true,
        syncStatus: "pending",
        serverConfirmed: false,
        serverAckAt: null,
        localUpdatedAt: deletedAt,
        deletedAt,
        deleted_at: deletedAt,
        deletedBy: session?.name || "",
        updatedAt: deletedAt,
        updatedBy: session?.name || "",
        pendingLocalDelete: true,
        tombstonePending: true,
      };
      return pendingDeleteRecord;
    }));
    if(pendingDeleteRecord) registerOrderLocalOperation(pendingDeleteRecord, "delete_order");
    // Manter selected/form aberto para mostrar estado DEL com ações bloqueadas.
    show("🗑️ Exclusão enviada — aguardando confirmação do servidor.");
    syncOnSave("orders");
  }

  // ── Totals ───────────────────────────────────────────────────────
  // ── STATUS BADGE ─────────────────────────────────────────────────
  const StatusBadge=({status,size=12})=>{
    const displayStatus = status === "Pendente" ? "Criado" : status;
    const sc=ORDER_STATUS_C[status]||ORDER_STATUS_C["Cancelado"];
    return <span style={{background:sc.bg,color:sc.text,border:`1px solid ${sc.dot}44`,
      padding:size===12?"2px 10px":"4px 14px",borderRadius:20,fontSize:size,fontWeight:700,whiteSpace:"nowrap"}}>
      {sc.icon} {displayStatus}
    </span>;
  };

    const renderOrderFormContent = (inlineMode=false) => form ? (()=>{
      const workingForm = ensureSectionModel(form);
      const sections = workingForm.costCenterSections || [];
      const repeatedCosts = new Set(sections.map(sec=>String(sec.costCenter||"").trim()).filter(Boolean).filter((cc,idx,arr)=>arr.indexOf(cc)!==idx));

      return <div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
          <Field label="Data"><Input type="date" value={workingForm.date} onChange={e=>setForm(f=>({...ensureSectionModel(f),date:e.target.value}))}/></Field>
          <Field label="Destinatários do pedido *" col="1/-1">
            {(()=>{
              const selectedRecipients = selectedPurchaseRecipients(workingForm);
              const selectedKeys = new Set(selectedRecipients.map(purchaseRecipientKey));
              const availableRecipients = purchaseRecipientOptions.filter(rec=>!selectedKeys.has(purchaseRecipientKey(rec)));
              const hasAdminRecipient = selectedRecipients.some(rec=>String(rec?.role || "").trim().toLowerCase()==="admin");
              return <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <Select value="" onChange={e=>{ addPurchaseRecipientToForm(e.target.value); e.target.value=""; }}>
                  <option value="">— Selecionar destinatário —</option>
                  {availableRecipients.map(rec=>{
                    const key = purchaseRecipientKey(rec);
                    const roleLabel = rec.role === "admin" ? "Admin" : rec.role === "gestor" ? "Gestor" : "Colaborador";
                    const noQuoteLabel = rec.canApprovePurchaseWithoutQuote ? " · aprova sem cotação" : " · sem aprovação sem cotação";
                    return <option key={key} value={key}>{rec.name || rec.username} · {roleLabel}{noQuoteLabel}</option>;
                  })}
                </Select>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",minHeight:30,alignItems:"center"}}>
                  {selectedRecipients.map(rec=>{
                    const key = purchaseRecipientKey(rec);
                    const roleLabel = rec.role === "admin" ? "Admin" : rec.role === "gestor" ? "Gestor" : "Colaborador";
                    const chipStyle = purchaseRecipientChipStyle(rec.role);
                    return <span key={key} style={{...chipStyle,display:"inline-flex",alignItems:"center",gap:8,borderRadius:999,padding:"7px 9px 7px 12px",fontSize:12,fontWeight:800}}>
                      <span>{rec.name || rec.username} · {roleLabel}</span>
                      <button type="button" onClick={()=>removePurchaseRecipientFromForm(key)} title="Remover destinatário" style={{width:18,height:18,borderRadius:"50%",border:"none",background:"rgba(255,255,255,.75)",color:"inherit",fontWeight:900,cursor:"pointer",lineHeight:"18px",padding:0}}>×</button>
                    </span>;
                  })}
                  {!selectedRecipients.length&&<span style={{fontSize:12,color:T.muted}}>Escolha quem deve receber/analisar este pedido.</span>}
                </div>
                <div style={{fontSize:11,color:hasAdminRecipient?T.muted:T.red}}>
                  {hasAdminRecipient ? "O solicitante recebe automaticamente. Os destinatários escolhidos também receberão notificações e Live Activities deste pedido." : "Obrigatório selecionar pelo menos 1 admin. O solicitante não aparece nesta lista porque já recebe automaticamente."}
                </div>
              </div>;
            })()}
          </Field>
          <Field label="Observações" col="1/-1"><Textarea rows={2} value={workingForm.notes} onChange={e=>setForm(f=>({...ensureSectionModel(f),notes:e.target.value}))} placeholder="Observações do pedido..."/></Field>
        </div>
        <div style={{borderTop:`1px solid ${T.border}`,paddingTop:16,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:T.text}}>Centros de custo do pedido</div>
              <div style={{fontSize:11,color:T.muted}}>Adicione um ou mais centros de custo e inclua itens separados por bloco.</div>
            </div>
            <div style={{display:"flex",gap:8}}><Btn size="sm" variant="ghost" onClick={addCostCenterSection}>+ Centro de Custo</Btn></div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {sections.map((section, sectionIdx)=>{
              const sectionProducts = visibleOrderableProductsForCostCenter(section.costCenter);
              const hasAccess = !!section.costCenter && canAccessPurchaseCostCenter(section.costCenter);
              return (
                <div key={section.id} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:10}}>
                    <div style={{display:"grid",gridTemplateColumns:"minmax(180px,240px) 1fr",gap:10,alignItems:"end",flex:1,minWidth:260}}>
                      <Field label={`Centro de Custo ${sectionIdx+1}`}>
                        <Select value={section.costCenter||""} disabled={bridgeScoped} onChange={e=>updateCostCenterSection(section.id,{costCenter:e.target.value,items:[defItem()]})}>
                          {(bridgeScoped ? [bridgeCostCenter] : allowedCostCenters).map(c=><option key={c} value={c}>{c}</option>)}
                        </Select>
                      </Field>
                      <div style={{fontSize:11,color: repeatedCosts.has(String(section.costCenter||"")) ? T.red : T.muted}}>
                        {repeatedCosts.has(String(section.costCenter||"")) ? "Este centro de custo já foi adicionado em outro bloco. Use um único bloco por centro." : (hasAccess ? `Produtos visíveis: ${sectionProducts.length}` : "Sem acesso a este centro de custo para Compras.")}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <Btn size="sm" onClick={()=>addSectionItem(section.id)} disabled={!hasAccess}>+ Item</Btn>
                      {sections.length>1&&<Btn size="sm" variant="danger" onClick={()=>removeCostCenterSection(section.id)}>Remover CC</Btn>}
                    </div>
                  </div>
                  {!hasAccess && <div style={{background:`${T.red}12`,border:`1px solid ${T.red}33`,color:T.red,borderRadius:10,padding:"10px 12px",fontSize:12,marginBottom:10}}>Você não tem acesso a este centro de custo no módulo Compras. Contate o administrador para liberar acesso.</div>}
                  {hasAccess && (section.items||[]).map((item,idx)=>{
                    const itemCategory = String(item.category || "Todas").trim() || "Todas";
                    const itemSubcategory = String(item.subcategory || "Todas").trim() || "Todas";
                    const productSearch = String(item.productSearch || "").trim().toLowerCase();
                    const categoryOptions = ["Todas", ...Array.from(new Set(sectionProducts.map(p=>String(p?.category || "").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR"))];
                    const productsAfterCategory = itemCategory !== "Todas"
                      ? sectionProducts.filter(p=>String(p?.category || "").trim() === itemCategory)
                      : sectionProducts;
                    const subcategoryOptions = ["Todas", ...Array.from(new Set(productsAfterCategory.map(p=>String(p?.subcategory || "").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR"))];
                    const filteredSectionProducts = productsAfterCategory.filter(p=>{
                      if(itemSubcategory !== "Todas" && String(p?.subcategory || "").trim() !== itemSubcategory) return false;
                      if(productSearch){
                        const hay = [p?.name, p?.brand, p?.category, p?.subcategory].map(v=>String(v||"").toLowerCase()).join(" ");
                        if(!hay.includes(productSearch)) return false;
                      }
                      return true;
                    });
                    return(
                      <div key={item.id} style={{background:T.card,borderRadius:12,padding:14,marginBottom:10,border:`1px solid ${T.border}`}}>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                          <Field label="Categoria">
                            <Select value={itemCategory} onChange={e=>{
                              const nextCategory = e.target.value;
                              const selectedProduct = sectionProducts.find(p=>String(p.id)===String(item.stockItemId||""));
                              const nextProductsAfterCategory = nextCategory !== "Todas"
                                ? sectionProducts.filter(p=>String(p?.category || "").trim() === nextCategory)
                                : sectionProducts;
                              const nextSubcategoryOptions = ["Todas", ...Array.from(new Set(nextProductsAfterCategory.map(p=>String(p?.subcategory || "").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR"))];
                              const nextSubcategory = nextSubcategoryOptions.includes(String(item.subcategory || "Todas")) ? String(item.subcategory || "Todas") : "Todas";
                              updateSectionItem(section.id,item.id,"category",nextCategory);
                              updateSectionItem(section.id,item.id,"subcategory",nextSubcategory);
                              if(selectedProduct){
                                const selectedCategory = String(selectedProduct?.category || "").trim();
                                const selectedSubcategory = String(selectedProduct?.subcategory || "").trim();
                                const categoryMismatch = nextCategory !== "Todas" && selectedCategory !== nextCategory;
                                const subcategoryMismatch = nextSubcategory !== "Todas" && selectedSubcategory !== nextSubcategory;
                                if(categoryMismatch || subcategoryMismatch){
                                  updateSectionItem(section.id,item.id,"stockItemId","");
                                  updateSectionItem(section.id,item.id,"description","");
                                }
                              }
                            }} style={{fontSize:12}}>{categoryOptions.map(c=><option key={c} value={c}>{c}</option>)}</Select>
                          </Field>
                          <Field label="Subcategoria">
                            <Select value={itemSubcategory} onChange={e=>{
                              const nextSubcategory = e.target.value;
                              const selectedProduct = sectionProducts.find(p=>String(p.id)===String(item.stockItemId||""));
                              updateSectionItem(section.id,item.id,"subcategory",nextSubcategory);
                              if(selectedProduct && nextSubcategory !== "Todas" && String(selectedProduct?.subcategory || "").trim() !== nextSubcategory){
                                updateSectionItem(section.id,item.id,"stockItemId","");
                                updateSectionItem(section.id,item.id,"description","");
                              }
                            }} style={{fontSize:12}}>{subcategoryOptions.map(c=><option key={c} value={c}>{c}</option>)}</Select>
                          </Field>
                        </div>
                        <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:10}}>
                          <div style={{flex:1}}>
                            <Field label={`Produto ${idx+1} *`}>
                              <Input value={item.productSearch||""} onChange={e=>updateSectionItem(section.id,item.id,"productSearch",e.target.value)} placeholder="Buscar produto por nome..." style={{marginBottom:6}}/>
                              <Select value={item.stockItemId||""} onChange={e=>{
                                const prod=filteredSectionProducts.find(p=>String(p.id)===e.target.value) || sectionProducts.find(p=>String(p.id)===e.target.value);
                                if(!prod){
                                  updateSectionItem(section.id,item.id,"stockItemId","");
                                  updateSectionItem(section.id,item.id,"description","");
                                  return;
                                }
                                updateSectionItem(section.id,item.id,"stockItemId",prod.id);
                                updateSectionItem(section.id,item.id,"description",prod.name+(prod.brand?` — ${prod.brand}`:""));
                                updateSectionItem(section.id,item.id,"unit",prod.unit||item.unit);
                                updateSectionItem(section.id,item.id,"category",prod.category||itemCategory||"Todas");
                                updateSectionItem(section.id,item.id,"subcategory",prod.subcategory||itemSubcategory||"Todas");
                                if(prod.unitCost) updateSectionItem(section.id,item.id,"price",prod.unitCost);
                              }}>
                                <option value="">— Selecione um produto —</option>
                                {filteredSectionProducts.length>0&&<optgroup label="── Produtos filtrados ──">{filteredSectionProducts.map(p=><option key={p.id} value={p.id}>{p.name}{p.brand?` — ${p.brand}`:""}</option>)}</optgroup>}
                              </Select>
                              {filteredSectionProducts.length===0 && <div style={{marginTop:6,fontSize:11,color:T.muted}}>Nenhum produto encontrado com os filtros atuais.</div>}
                              {item.stockItemId&&<Input value={item.description} onChange={e=>updateSectionItem(section.id,item.id,"description",e.target.value)} placeholder="Obs adicional (opcional)" style={{marginTop:6,fontSize:12,color:T.muted}}/>}
                            </Field>
                          </div>
                          <button onClick={()=>removeSectionItem(section.id,item.id)} style={{background:"none",border:`1px solid ${T.red}`,borderRadius:7,padding:"8px 10px",cursor:"pointer",color:T.red,marginTop:20,flexShrink:0}}>×</button>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"70px 80px",gap:8,marginBottom:4}}>
                          <Field label="Qtd"><Input type="number" min="0" value={item.quantity} onChange={e=>updateSectionItem(section.id,item.id,"quantity",Number(e.target.value))}/></Field>
                          <Field label="Unid."><Select value={item.unit} onChange={e=>updateSectionItem(section.id,item.id,"unit",e.target.value)} style={{fontSize:12}}>{(SL.UNITS||UNITS).map(u=><option key={u}>{u}</option>)}</Select></Field>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
          <div>{!workingForm._new&&canDelete&&<Btn variant="danger" onClick={()=>{del(workingForm.id);setForm(null);}}>🗑️ Excluir</Btn>}</div>
          <div style={{display:"flex",gap:10}}><Btn variant="ghost" onClick={()=>setForm(null)}>{inlineMode?"Fechar":"Cancelar"}</Btn>{((workingForm&&workingForm._new&&canCreate)||(workingForm&&!workingForm._new&&canEdit))&&<Btn onClick={saveOrder}>Salvar Pedido</Btn>}</div>
        </div>
      </div>;
    })() : null;


  const renderNewOrderInline = supportsInlineNew && form ? (
    <InlinePanelShell T={T} title="🛒 Novo Pedido por Centro de Custo">
      <div style={{background:`${T.accent}12`,border:`1px solid ${T.accent}33`,borderRadius:12,padding:"12px 14px",marginBottom:14,fontSize:12,color:T.text,lineHeight:1.5}}>
        Monte o pedido por <b>centro de custo</b>. Cada bloco mostra apenas os produtos disponíveis para o centro escolhido e respeita o acesso do usuário.
      </div>
      {renderOrderFormContent(true)}
    </InlinePanelShell>
  ) : null;

  const showPurchaseList = !selected && !form;

  return <div>
    <Toast toast={toast}/>

    {/* Lightbox */}
    {lightbox&&(
      <div onClick={()=>setLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12}}>
        <img src={lightbox.photos[lightbox.idx]} alt="" style={{maxWidth:"90vw",maxHeight:"80vh",objectFit:"contain",borderRadius:10}}/>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {lightbox.photos.length>1&&<>
            <button onClick={e=>{e.stopPropagation();setLightbox(l=>({...l,idx:(l.idx-1+l.photos.length)%l.photos.length}));}} style={{background:"none",border:"none",color:"#fff",fontSize:28,cursor:"pointer"}}>‹</button>
            <span style={{color:"#ccc",fontSize:13}}>{lightbox.idx+1}/{lightbox.photos.length}</span>
            <button onClick={e=>{e.stopPropagation();setLightbox(l=>({...l,idx:(l.idx+1)%l.photos.length}));}} style={{background:"none",border:"none",color:"#fff",fontSize:28,cursor:"pointer"}}>›</button>
          </>}
        </div>
        <div style={{color:"#888",fontSize:12}}>Clique em qualquer lugar para fechar</div>
      </div>
    )}

    {/* ── COTAÇÃO MODAL ──────────────────────────────────────────── */}
    {cotacaoModal && !selected && (()=>{
      const _cOrder=orders.find(o=>o.id===cotacaoModal.orderId);
      if(!_cOrder) return null;
      return <CotacaoModal order={_cOrder} suppliers={supplierDirectory} show={show} setLightbox={setLightbox}
        onClose={()=>setCotacao(null)}
        onSave={cot=>saveCotacao(_cOrder.id,cot)}/>;
    })()}

    {noQuoteModal && !selected && (
      <Modal open={true} onClose={()=>setNoQuoteModal(null)} title="Aprovar sem cotação">
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>
            Selecione a justificativa obrigatória para aprovar esta compra sem cotação registrada.
          </div>
          <Field label="Justificativa *">
            <Select value={noQuoteModal.reason||""} onChange={e=>setNoQuoteModal(m=>({...m,reason:e.target.value}))}>
              <option value="">Selecione...</option>
              {noQuoteReasons.map(reason=><option key={reason} value={reason}>{reason}</option>)}
            </Select>
          </Field>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setNoQuoteModal(null)}>Cancelar</Btn>
            <Btn onClick={()=>approveWithoutQuote(noQuoteModal.orderId, noQuoteModal.reason)}>✅ Aprovar sem cotação</Btn>
          </div>
        </div>
      </Modal>
    )}

    {/* ── RECEIVE MODAL ──────────────────────────────────────────── */}
    {receiveModal&&(()=>{
      const _rOrder=orders.find(o=>o.id===receiveModal.orderId);
      if(!_rOrder) return null;
      return <ReceiveModal order={_rOrder} setLightbox={setLightbox}
        onClose={()=>setReceive(null)}
        onSave={data=>receiveItems(_rOrder.id,data)}/>;
    })()}

    {/* ── APPROVE PANEL (inside detail view) ─────────────────────── */}

    {/* ── HEADER ─────────────────────────────────────────────────── */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
      <div>
        <h2 style={{margin:0,color:T.text,fontSize:20,fontWeight:800}}>🛒 Módulo de Compras</h2>
        <div style={{fontSize:12,color:T.muted,marginTop:2}}>{activeOrders.length} pedido(s) · {activeOrders.filter(o=>o.status==="Pendente").length} criados · {activeOrders.filter(o=>o.status==="Em Trânsito").length} a receber</div>
      </div>
      {showPurchaseList&&canCreate&&!supportsInlineNew&&<Btn onClick={openNew}>+ Novo Pedido</Btn>}
    </div>

    {renderNewOrderInline}

    {showPurchaseList && !supportsInlineNew && (()=>{
      const purchaseSummaryCards = [
        { id:"orders", icon:"🛒", label:"Pedidos", value:activeOrders.length, color:T.accent },
        { id:"open_orders", icon:"📂", label:"Pedidos em Aberto", value:activeOrders.filter(o=>!["Concluído","Cancelado","Negado"].includes(String(o.status||""))).length, color:T.orange },
        { id:"missing_approval", icon:"⏳", label:"Faltando Aprovação", value:activeOrders.filter(o=>orderHasPendingApproval(o)).length, color:T.red },
        { id:"in_transit", icon:"🚚", label:"Em Trânsito", value:activeOrders.filter(o=>String(o.status||"")==="Em Trânsito").length, color:T.blue || T.accent },
        { id:"quotes", icon:"💰", label:"Cotações", value:activeOrders.filter(o=>String(o?.status||"")!=="Negado" && (o.cotacoes||[]).length>0).length, color:T.yellow },
        { id:"denied_orders", icon:"❌", label:"Negados", value:activeOrders.filter(o=>String(o?.status||"")==="Negado").length, color:T.red },
        { id:"approvals", icon:"✅", label:"Aprovações", value:activeOrders.filter(o=>["Em Análise","Em Trânsito","Concluído"].includes(String(o.status||""))).length, color:T.green },
        { id:"suppliers", icon:"🏪", label:"Fornecedores", value:supplierDirectory.length, color:T.purple },
      ].filter(card=>allowedPurchaseCards.includes(card.id));
      return purchaseSummaryCards.length ? (
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:14,marginBottom:18}}>
          {purchaseSummaryCards.map(card=><div key={card.id}><StatCard icon={card.icon} label={card.label} value={card.value} color={card.color} onClick={()=>setCardFilter(card.id)}/></div>)}
        </div>
      ) : null;
    })()}

    {/* ── FILTERS ────────────────────────────────────────────────── */}
    {showPurchaseList && <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16,alignItems:"center"}}>
      <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Buscar por número, item, solicitante ou centro de custo..." style={{flex:"1 1 160px",minWidth:120}}/>
      <Select value={filterStatus} onChange={e=>{ setCardFilter("all"); setFs(e.target.value); }} style={{width:"auto"}}>
        <option value="Todos">Todos os status</option>
        {ORDER_STATUSES.map(s=><option key={s} value={s}>{s==="Pendente"?"Criado":s}</option>)}
      </Select>
      <Select value={bridgeScoped ? bridgeCostCenter : filterCC} disabled={bridgeScoped} onChange={e=>setFcc(e.target.value)} style={{width:"auto"}}>
        <option value="Todos">Todos os CCs</option>
        {(bridgeScoped ? [bridgeCostCenter] : allowedCostCenters).map(c=><option key={c}>{c}</option>)}
      </Select>
    </div>}

    {/* ── ORDER LIST ─────────────────────────────────────────────── */}
    {showPurchaseList&& !supportsInlineNew && <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {filtered.map(o=>{
        const cotacoes=o.cotacoes||[];
        const displayCode = getPurchaseDisplayRecordNumber(o) || o.id;
        return(
          <div key={o.id} onClick={()=>openOrderDetail(o)} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 18px",cursor:"pointer",transition:"all .15s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
            onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <div>
                <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayCode}</div>
                <div style={{fontWeight:700,color:T.text,fontSize:14}}>Pedidos do Centro {o.costCenter || "Sem centro"}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>👤 {o.requester} · {fmtDate(o.date)}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                {o.pendingLocalDelete && (
                  <span style={{fontSize:10,fontWeight:800,color:"#e53e3e",
                    background:"#fff5f5",border:"1px solid #feb2b2",
                    padding:"2px 8px",borderRadius:999,letterSpacing:.4}}>
                    🗑️ DEL
                  </span>
                )}
                <StatusBadge status={o.status}/>
                {cotacoes.length>0&&<span style={{fontSize:10,color:T.muted}}>💰 {cotacoes.length} cotação(ões)</span>}
                {(()=>{ const hasItemPhotos=(o.items||[]).some(it=>getPhotos(it).length>0); return (
                  <span title={hasItemPhotos?"Pedido com fotos nos itens":"Pedido sem fotos nos itens"} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 8px",borderRadius:999,border:`1px solid ${hasItemPhotos?`${T.accent}44`:T.border}`,background:(hasItemPhotos?`${T.accent}14`:T.card2),color:(hasItemPhotos?T.accent:T.muted),fontSize:11,fontWeight:700}}><ServicePhotoStatusIcon size={16} hasPhotos={hasItemPhotos}/></span>
                )})()}
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:6}}>
              {(o.items||[]).slice(0,4).map((item,i)=>{
                const itemStatus = normalizeItemStatus(item?.itemStatus || item?.status, o?.status);
                const tone = PURCHASE_ITEM_STATUS_META[itemStatus] || PURCHASE_ITEM_STATUS_META["Analisando"];
                const partialInfo = itemStatus === "Parcial" && String(item?.authorizedQuantity || "").trim() ? ` · Autorizado: ${item.authorizedQuantity}` : "";
                return (
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,fontSize:11,background:T.card2,border:`1px solid ${T.border}`,padding:"6px 8px",borderRadius:10,color:T.muted}}>
                    <div style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.description} ×{item.quantity}{partialInfo}</div>
                    <span style={{fontSize:10,fontWeight:700,color:tone.color,background:tone.bg,border:tone.border,padding:"3px 8px",borderRadius:999,flexShrink:0}}>{itemStatus}</span>
                  </div>
                );
              })}
              {(o.items||[]).length>4&&<span style={{fontSize:11,color:T.muted}}>+{(o.items||[]).length-4} itens</span>}
            </div>
            {/* Action on card: list only opens the detail/análise flow. */}
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:10}} onClick={e=>e.stopPropagation()}>
              <Btn size="sm" onClick={()=>openOrderDetail(o)}>✅ Analisar</Btn>
            </div>
          </div>
        );
      })}
      {filtered.length===0&&<div style={{color:T.muted,textAlign:"center",padding:48,fontSize:14}}>
        Nenhum pedido encontrado. <span style={{color:T.accent,cursor:"pointer"}} onClick={openNew}>Criar novo pedido →</span>
      </div>}
    </div>}

    {/* ── ORDER DETAIL VIEW ──────────────────────────────────────── */}
    {selected&&(()=>{
      const o=orders.find(x=>x.id===selected.id)||selected;
      const cotacoes=o.cotacoes||[];
      const receipts=o.receipts||[];
      const showingInlineEdit = !!(form && !form._new && String(form.id)===String(o.id));
      const showingInlineCotacao = !!(cotacaoModal && String(cotacaoModal.orderId)===String(o.id));
      const showingInlineDeny = !!(denyModal && String(denyModal.orderId)===String(o.id));
      const showingInlineNoQuote = !!(noQuoteModal && String(noQuoteModal.orderId)===String(o.id));
      const displayCode = getPurchaseDisplayRecordNumber(o) || o.id;

      return(
        <div>
          {/* Back */}
          <button onClick={()=>{ if(onClearOpenOrder) onClearOpenOrder(); if(onBridgeBack) onBridgeBack(); else setSelected(null); }} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
            ← Voltar para lista
          </button>

          {/* Header */}
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div>
                <div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:4}}>{displayCode}</div>
                <div style={{fontWeight:800,color:T.text,fontSize:18}}>Pedido {(farms.find(f=>String(f.id)===String(o.farmId||currentFarmId))?.name) || o.farmName || "Sem fazenda"}</div>
                <div style={{fontSize:12,color:T.muted,marginTop:2}}>👤 {o.requester} · 📅 {fmtDate(o.date)} · 🏠 {o.costCenter}</div>
              </div>
              <StatusBadge status={o.status} size={13}/>
            </div>
            {o.notes&&<div style={{fontSize:12,color:T.muted,background:T.card2,padding:"8px 12px",borderRadius:8}}>{o.notes}</div>}

            {/* Workflow steps */}
            <div style={{display:"flex",gap:0,marginTop:14,overflowX:"auto"}}>
              {[
                {s:"Pendente",icon:"🕐",label:"Criado"},
                {s:"Em Análise",icon:"🔍",label:"Cotação"},
                {s:"Aprovado",icon:"✅",label:"Aprovado"},
                {s:"Em Trânsito",icon:"🚚",label:"Enviado"},
                {s:"Concluído",icon:"📦",label:"Recebido"},
              ].map((step,idx,arr)=>{
                const statuses=["Pendente","Em Análise","Aprovado","Em Trânsito","Concluído","Negado","Cancelado"];
                const currentIdx=statuses.indexOf(o.status);
                const stepIdx=statuses.indexOf(step.s);
                const done=currentIdx>=stepIdx||o.status==="Concluído";
                const neg=o.status==="Negado"&&step.s==="Aprovado";
                return(
                  <div key={step.s} style={{display:"flex",alignItems:"center",flex:1,minWidth:60}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",flex:1}}>
                      <div style={{width:32,height:32,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,
                        background:neg?T.red:done?T.green:T.card2,
                        border:`2px solid ${neg?T.red:done?T.green:T.border}`,
                        color:done?"#fff":"auto",transition:"all .3s"}}>
                        {neg?"❌":step.icon}
                      </div>
                      <div style={{fontSize:9,color:done?T.green:T.muted,marginTop:3,fontWeight:done?700:400,textAlign:"center"}}>{neg?"Negado":step.label}</div>
                    </div>
                    {idx<arr.length-1&&<div style={{height:2,width:"100%",background:done?T.green:T.border,flex:2,transition:"all .3s"}}/>}
                  </div>
                );
              })}
            </div>

            {/* v3.2.12: banner DEL pendente */}
            {o.pendingLocalDelete && (
              <div style={{
                background:"#fff5f5",border:"1px solid #feb2b2",
                borderRadius:8,padding:"10px 14px",marginTop:12,
                fontSize:13,color:"#c53030",fontWeight:600,
                display:"flex",alignItems:"center",gap:8,
              }}>
                🗑️ Exclusão pendente — aguardando confirmação do servidor.
                Os dados serão preservados para auditoria no servidor.
              </div>
            )}

            {/* Action buttons */}
            {!o.pendingLocalDelete && (
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:14}}>
              {(o.status==="Pendente"||o.status==="Em Análise")&&
                <Btn onClick={()=>setCotacao({orderId:o.id})}>💰 Registrar Cotação</Btn>}
              {o.status==="Em Trânsito"&&(o.requester===session?.name||canApprove)&&
                <Btn onClick={()=>setReceive({orderId:o.id})}>📦 Receber Itens</Btn>}
              {canApprove&&o.status==="Em Análise"&&
                <Btn variant="danger" onClick={()=>setDenyModal({orderId:o.id, reason:denyReasons[0]||"Outro motivo", customReason:"", finalReason:denyReasons[0]||"Outro motivo"})}>❌ Negar</Btn>}
              {canApprove&&["Pendente","Em Análise"].includes(o.status)&&!cotacoes.length&&canApproveWithoutQuote&&
                <Btn onClick={()=>setNoQuoteModal({orderId:o.id,reason:o.noQuoteReason||""})}>⚡ Aprovar sem cotação</Btn>}
              {(o.status==="Pendente"||o.status==="Em Análise")&&(canApprove||canUserEditExistingOrder(o)|| (canEdit && isOwnOrder(o)))&&
                <Btn variant="ghost" onClick={()=>openEditOrder(o)}>✏️ Editar</Btn>}
              {canDelete&&<Btn variant="danger" onClick={()=>del(o.id)}>🗑️ Excluir</Btn>}
            </div>
            )}
            {o.denyReason&&<div style={{background:`${T.red}15`,border:`1px solid ${T.red}44`,borderRadius:8,padding:"8px 12px",marginTop:10,fontSize:12,color:T.red}}>
              ❌ Negado por {o.deniedBy} · Motivo: {o.denyReason}
            </div>}
            {o.approvedWithoutQuote&&o.noQuoteReason&&<div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"8px 12px",marginTop:10,fontSize:12,color:T.yellow}}>
              ⚠️ Aprovado sem cotação por {o.approvedBy} · Justificativa: {o.noQuoteReason}
            </div>}
          </div>

          {showingInlineEdit && (
            <InlinePanelShell T={T} title="✏️ Editar Pedido">
              {renderOrderFormContent(true)}
            </InlinePanelShell>
          )}
          {showingInlineCotacao && (
            <InlineCotacaoPanel
              T={T}
              order={o}
              suppliers={supplierDirectory}
              Field={Field}
              Input={Input}
              Select={Select}
              show={show}
              onDraftChange={setCotacaoDraft}
            />
          )}
          {showingInlineNoQuote && (
            <InlineNoQuotePanel
              T={T}
              Field={Field}
              Select={Select}
              reasons={noQuoteReasons}
              reason={noQuoteModal?.reason||""}
              onReasonChange={(reason)=>setNoQuoteModal(m=>({...m,reason}))}
            />
          )}
          {showingInlineDeny && (
            <InlineDenyPanel
              T={T}
              denyReasons={denyReasons}
              Btn={Btn}
              Field={Field}
              Select={Select}
              Textarea={Textarea}
              onClose={()=>setDenyModal(null)}
              reason={denyModal?.reason}
              customReason={denyModal?.customReason}
              onReasonChange={(next)=>setDenyModal(prev=>({...prev, orderId:o.id, ...next}))}
            />
          )}

          {/* Items */}
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",marginBottom:16}}>
            <div style={{fontWeight:700,color:T.text,fontSize:15,marginBottom:12}}>📋 Itens do Pedido</div>
            {(o.items||[]).map(item=>{
              const draftItem = getApprovalDraftItemsForOrder(o.id)?.[item.id] || getApprovalDraftItemsForOrder(o.id)?.[String(item.id)] || null;
              const displayItem = draftItem ? {...item,...draftItem} : item;
              const itemCotacoes=cotacoes.filter(c=>(c.items||[]).find(ci=>String(ci.itemId)===String(item.id)));
              const approved=displayItem.supplierName||displayItem.approvedSupplier;
              return(
                <div key={item.id} style={{background:T.card2,borderRadius:10,padding:"12px 14px",marginBottom:8,border:`1px solid ${approved?T.green+"44":T.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                        <div style={{fontWeight:600,color:T.text,fontSize:13,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.description}</div>
                        {(()=>{ const itemStatus = normalizeItemStatus(displayItem?.itemStatus || displayItem?.status, o?.status); const tone = PURCHASE_ITEM_STATUS_META[itemStatus] || PURCHASE_ITEM_STATUS_META["Analisando"]; const authQty = String(displayItem?.authorizedQuantity || "").trim(); return <span style={{fontSize:11,fontWeight:700,color:tone.color,background:tone.bg,border:tone.border,padding:"4px 8px",borderRadius:999,flexShrink:0}}>{itemStatus}{itemStatus === "Parcial" && authQty ? ` · ${authQty}` : ""}</span>; })()}
                      </div>
                      <div style={{fontSize:11,color:T.muted,marginTop:2}}>Qtd: {item.quantity} {item.unit} · Cat: {item.category}</div>
                      {canApprove && (
                        <div style={{display:"grid",gap:6,marginTop:8}}>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {PURCHASE_ITEM_STATUS_OPTIONS.map(statusOpt=>{
                              const active = normalizeItemStatus(displayItem?.itemStatus || displayItem?.status, o?.status) === statusOpt;
                              return (
                                <button
                                  key={statusOpt}
                                  type="button"
                                  onClick={()=>updateOrderItemStatusDraft(o.id, item.id, statusOpt)}
                                  style={{
                                    border:`1px solid ${active ? (PURCHASE_ITEM_STATUS_META[statusOpt]?.color || T.accent) : T.border}`,
                                    background:active ? (PURCHASE_ITEM_STATUS_META[statusOpt]?.bg || `${T.accent}18`) : T.card,
                                    color:active ? (PURCHASE_ITEM_STATUS_META[statusOpt]?.color || T.accent) : T.muted,
                                    borderRadius:999,
                                    padding:"5px 10px",
                                    fontSize:11,
                                    fontWeight:700,
                                    cursor:"pointer"
                                  }}
                                >
                                  {statusOpt}
                                </button>
                              );
                            })}
                          </div>
                          <div style={{fontSize:11,color:T.muted}}>Ao marcar <b>Parcial</b>, digite a <b>quantidade autorizada</b> para o colaborador saber exatamente o que foi aprovado.</div>
                        </div>
                      )}
                      {approved&&<div style={{fontSize:11,color:T.green,marginTop:8}}>✅ Fornecedor aprovado: {approved} · R$ {fmtMoney(item.approvedPrice||item.price)}</div>}
                      {item.qtyReceived>0&&<div style={{fontSize:11,color:T.accent,marginTop:2}}>📦 Recebido: {item.qtyReceived}/{item.quantity}</div>}
                    </div>
                  </div>
                  {getPhotos(item).length>0&&(
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8,marginBottom:4}}>
                      {getPhotos(item).map((ph,pi)=>{
                        const preview = ph.thumb || ph.photo || "";
                        const full = ph.photo || ph.thumb || "";
                        return (
                          <div key={ph.id||pi} title={ph.title||"Foto do item"} style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start",maxWidth:90}}>
                            <button type="button" onClick={()=>full&&setLightbox({photos:getPhotos(item).map(x=>x.photo||x.thumb||"").filter(Boolean),idx:pi})} style={{padding:0,background:"none",border:"none",cursor:full?"zoom-in":"default"}}>
                              {preview ? <img src={preview} alt={ph.title||"Foto do item"} style={{width:72,height:72,objectFit:"cover",borderRadius:10,border:`1px solid ${T.border}`,display:"block"}}/> : <div style={{width:72,height:72,borderRadius:10,border:`1px solid ${T.border}`,background:T.card,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,fontSize:11}}>Sem foto</div>}
                            </button>
                            <div style={{fontSize:10,color:T.muted,lineHeight:1.25,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:72}}>{ph.title||ph.name||"Foto"}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Cotações for this item */}
                  {itemCotacoes.length>0&&(
                    <div style={{marginTop:8}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:4}}>💰 Cotações recebidas:</div>
                      {itemCotacoes.map(cot=>{
                        const cotItem=(cot.items||[]).find(ci=>String(ci.itemId)===String(item.id));
                        const isSelected=String(item.approvedCotacaoId)===String(cot.id)&&String(item.approvedItemId)===String(item.id);
                        return(
                          <div key={cot.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                            padding:"6px 10px",borderRadius:8,marginBottom:4,
                            background:isSelected?`${T.green}18`:`${T.card}`,
                            border:`1px solid ${isSelected?T.green:T.border}44`}}>
                            <div style={{fontSize:12}}>
                              <strong>{cot.supplierName}</strong> · R$ {fmtMoney(cotItem?.price||0)}
                              {cot.obs&&<span style={{color:T.muted}}> · {cot.obs}</span>}
                              <span style={{color:T.muted,fontSize:10}}> · {cot.by}</span>
                            </div>
                            {canApprove&&o.status==="Em Análise"&&(
                              <Btn size="sm" variant={isSelected?"ghost":"primary"}
                                onClick={()=>{
                                  // Approve this item from this cotacao
                                  const _iTS=new Date().toISOString();
                                  const updatedItems=(o.items||[]).map(i=>
                                    String(i.id)===String(item.id)
                                      ?{...i,approvedCotacaoId:cot.id,approvedItemId:item.id,approvedPrice:cotItem?.price||i.price,supplierName:cot.supplierName}
                                      :i
                                  );
                                  const allApproved=updatedItems.every(i=>i.supplierName||i.approvedSupplier);
                                  applyNormalizedOrders((orders || []).map(ord=>ord.id!==o.id?ord:markPurchaseUpdatePending({...ord,items:updatedItems,updatedAt:_iTS}, _iTS, "select_quote_item")));
                                  syncOnSave("orders");
                                  show(allApproved?"✅ Todos os itens aprovados! Clique em Aprovar Pedido.":"✅ Item aprovado!");
                                }}>
                                {isSelected?"✅ Selecionado":"Selecionar"}
                              </Btn>
                            )}
                            {(cot.files?.length>0||cot.photos?.length>0)&&<button onClick={()=>{ if(cot.files?.length>0){ window.open(cot.files[0].url||cot.files[0].dataUrl||cot.photos?.[0],"_blank"); } else { setLightbox({photos:cot.photos,idx:0}); } }} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:11}}>{cot.files?.length>0?"📄":"📷"}</button>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {["Pendente","Em Análise"].includes(o.status)
              && (approvalDraft?.orderId === o.id || noQuoteModal?.orderId === o.id || cotacaoModal?.orderId === o.id || denyModal?.orderId === o.id)
              && (canApprove || String(cotacaoModal?.orderId || "") === String(o.id)) && (
              <div style={{marginTop:12,padding:"12px 14px",background:`${T.orange}12`,border:`1px solid ${T.orange}44`,borderRadius:12,display:"flex",flexDirection:"column",gap:12}}>
                <span style={{fontSize:12,color:T.orange,lineHeight:1.45}}>
                  ⚠️ Alterações pendentes. Confirme no final para salvar e atualizar a Live Activity.
                </span>
                <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <Btn variant="ghost" onClick={()=>cancelUnifiedPurchaseFlow(o.id)} style={{minWidth:128,justifyContent:"center"}}>Cancelar</Btn>
                  <Btn onClick={()=>confirmUnifiedPurchaseFlow(o.id)} style={{minWidth:128,justifyContent:"center"}}>Confirmar</Btn>
                </div>
              </div>
            )}

            {/* Approve all button */}
            {canApprove&&o.status==="Em Análise"&&(o.items||[]).every(i=>i.supplierName)&&(
              <div style={{marginTop:14,padding:"14px",background:`${T.green}12`,border:`1px solid ${T.green}44`,borderRadius:10}}>
                <div style={{fontSize:13,color:T.green,fontWeight:700,marginBottom:8}}>✅ Todos os itens têm fornecedor aprovado!</div>
                <div style={{fontSize:11,color:T.muted,marginBottom:10}}>
                  {(o.items||[]).map(i=><span key={i.id} style={{marginRight:8}}>• {i.description}: {i.supplierName} (R$ {fmtMoney(i.approvedPrice||i.price)})</span>)}
                </div>
                <Btn onClick={()=>approveOrder(o.id,(o.items||[]).map(i=>({itemId:i.id,cotacaoId:i.approvedCotacaoId,supplierName:i.supplierName,approvedPrice:i.approvedPrice||i.price})))}>
                  ✅ Aprovar Pedido → Em Trânsito
                </Btn>
              </div>
            )}
          </div>

          {/* Receipts history */}
          {receipts.length>0&&(
            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",marginBottom:16}}>
              <div style={{fontWeight:700,color:T.text,fontSize:15,marginBottom:12}}>📄 Histórico de Recebimentos</div>
              {receipts.map((rec,i)=>(
                <div key={i} style={{background:T.card2,borderRadius:10,padding:"10px 14px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:600,color:T.text}}>Recebimento #{i+1} · NF {rec.nfNum||"s/n"}</div>
                    <div style={{fontSize:11,color:T.muted}}>{rec.by} · {rec.ts?new Date(rec.ts).toLocaleString("pt-BR"):""}</div>
                  </div>
                  {rec.nfPhotos?.length>0&&<button onClick={()=>setLightbox({photos:rec.nfPhotos,idx:0})} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:11,padding:0}}>📷 Ver nota fiscal</button>}
                </div>
              ))}
            </div>
          )}

          {/* Cotações audit log */}
          {cotacoes.length>0&&(
            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}>
              <div style={{fontWeight:700,color:T.text,fontSize:15,marginBottom:12}}>💰 Histórico de Cotações (Auditoria)</div>
              {cotacoes.map((cot,i)=>(
                <div key={cot.id||i} style={{background:T.card2,borderRadius:10,padding:"10px 14px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:600,color:T.text}}>🏭 {cot.supplierName}</div>
                    <div style={{fontSize:11,color:T.muted}}>{cot.by} · {cot.ts?new Date(cot.ts).toLocaleString("pt-BR"):""}</div>
                  </div>
                  {(cot.items||[]).map(ci=>{
                    const item=(o.items||[]).find(i=>String(i.id)===String(ci.itemId));
                    return item?(
                      <div key={ci.itemId} style={{fontSize:12,color:T.muted,marginLeft:8}}>
                        • {item.description}: R$ {fmtMoney(ci.price)}
                      </div>
                    ):null;
                  })}
                  {cot.obs&&<div style={{fontSize:11,color:T.muted,marginTop:4,fontStyle:"italic"}}>{cot.obs}</div>}
                  {(cot.files?.length>0||cot.photos?.length>0)&&<button onClick={()=>{ if(cot.files?.length>0){ window.open(cot.files[0].url||cot.files[0].dataUrl||cot.photos?.[0],"_blank"); } else { setLightbox({photos:cot.photos,idx:0}); } }} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:11,padding:0,marginTop:4}}>{cot.files?.length>0?"📄 Ver orçamento PDF":"📷 Ver orçamento"}</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    })()}

    {/* ── NEW / EDIT ORDER MODAL ──────────────────────────────────── */}


  </div>;
}

}
